import { payrollEn, payrollPtBr } from '@/lib/i18n/fpa-payroll';
/**
 * FP&A DRE viewer (ALC-1911). Domain terms stay in Portuguese in both locales
 * per packages/services/src/fpa/CONTEXT.md: Regime Competência/Caixa, Fonte
 * Contábil/Gerencial, Cenário, Não Classificado, Sem Evento de Caixa.
 */
export const fpaPtBr = {
  payroll: payrollPtBr,
  list: {
    title: 'FP&A — DRE',
    description:
      'Templates de DRE avaliados sobre o razão contábil ou as classificações gerenciais.',
    showArchived: 'Mostrar arquivados',
    columns: {
      name: 'Template',
      updatedAt: 'Atualizado em',
    },
    filters: {
      columnTooltip: 'Filtrar coluna',
      noBase: 'Sem base',
      all: 'Todos',
    },
    archivedBadge: 'Arquivado',
    empty: {
      title: 'Nenhum template de DRE',
      description:
        'Crie um template no construtor ou abra um existente. Assim que um template for criado, ele aparece aqui.',
    },
    emptyArchivedHint: 'Nenhum template ativo. Ative "Mostrar arquivados" para ver os arquivados.',
    noMatch: {
      title: 'Nenhum template corresponde aos filtros',
      description: 'Ajuste os filtros das colunas para ver outros templates.',
      clear: 'Limpar filtros',
    },
    loading: 'Carregando templates…',
    error: 'Erro ao carregar os templates.',
    retry: 'Tentar novamente',
  },
  meta: {
    regime: 'Regime',
    fonte: 'Fonte',
    dateBasis: 'Base de Data',
    regimeValues: {
      COMPETENCIA: 'Competência',
      CAIXA: 'Caixa',
    },
    fonteValues: {
      CONTABIL: 'Contábil',
      GERENCIAL: 'Gerencial',
    },
    dateBasisValues: {
      COMPETENCIA: 'Competência',
      GERENCIAL: 'Gerencial',
    },
    scenarioValues: {
      REALIZADO: 'Realizado',
      ORCADO: 'Orçado',
      REFORECAST: 'Reforecast',
      RECEITA_PROJETADA: 'Receita Projetada',
      DESPESA_PROJETADA: 'Despesa Projetada',
      PROJECAO: 'Projeções',
    },
  },
  report: {
    reportingCurrency: 'Moeda de apresentação',
    functionalCurrency: 'Moeda funcional',
    fxDetails: 'Câmbio aplicado · {{currency}}',
    fxPolicy:
      'Relatório combinado, sem eliminações entre empresas. Realizado usa a PTAX da data do evento; métricas mensais usam o fechamento do mês. Cotações indisponíveis impedem a conversão. Os lançamentos contábeis permanecem inalterados.',
    fxAsOf: 'Cotações limitadas a {{date}}.',
    fxQuoteDate: 'PTAX de {{date}} · evento em {{requested}}',
    fxNoConversion: 'Os valores já estão na moeda de apresentação.',
    fxSnapshotUnavailable:
      'Para congelar, selecione uma entidade na moeda funcional. O congelamento consolidado ainda não está disponível.',

    backToList: 'Templates',
    header: {
      minimize: 'Minimizar cabeçalho',
      expand: 'Expandir cabeçalho',
    },
    periodFrom: 'De',
    periodTo: 'Até',
    combinedBadge: 'Combinado (sem eliminações)',
    lineColumn: 'Linha',
    refreshing: 'Atualizando avaliação…',
    error: 'Erro ao avaliar o template.',
    errors: {
      reportingCurrencyRequired:
        'Selecione uma moeda de apresentação para combinar entidades com moedas diferentes.',
      reportingFxUnavailable:
        'Não há PTAX disponível para converter este relatório. Nenhuma cotação foi estimada. Tente novamente mais tarde.',
      reportingScopeChanged: 'Os livros ou as moedas das entidades mudaram. Atualize o relatório.',
      reportingScenarioUnsupported:
        'A apresentação consolidada está disponível para Realizado. Para comparar cenários, selecione uma entidade na moeda funcional.',
      multiCurrencyScope:
        'As entidades selecionadas usam moedas funcionais diferentes — valores nunca são somados entre moedas. Selecione entidades de uma única moeda.',
      bookAmbiguous:
        'Não foi possível inferir o livro contábil: as entidades selecionadas não compartilham um único livro. Selecione entidades que usem o mesmo livro ou selecione uma moeda de apresentação.',
      columnsRequired:
        'Este template ainda não tem colunas padrão. Adicione uma coluna na aba Colunas para avaliar o relatório.',
      bookNotAssigned:
        'O livro solicitado não está atribuído a todas as entidades selecionadas. Ajuste a seleção de entidades ou a configuração do livro.',
      bookNotFound: 'O livro contábil solicitado não existe ou foi removido.',
      entityScopeRequired: 'Selecione ao menos uma entidade para avaliar o template.',
      scenarioEntityScope:
        'O orçamento selecionado pertence a uma entidade fora do escopo desta avaliação. Inclua a entidade do orçamento na seleção.',
      scenarioBookMismatch:
        'O orçamento selecionado está vinculado a outro livro contábil. Compare orçamento e realizado no mesmo livro.',
      scenarioCurrencyMismatch:
        'A moeda do orçamento difere da moeda do escopo avaliado — valores nunca são somados entre moedas.',
      scenarioUnattributable:
        'O orçamento possui linhas sem conta compatível com a Fonte deste template (ex.: envelopes por centro de custo ou linha "Outros"); a coluna omitiria parte do orçamento.',
      scenarioDimensionOverlap:
        'O orçamento planeja a mesma conta em granularidades de dimensão diferentes; somá-las duplicaria o planejado.',
      scenarioKindMismatch:
        'O tipo de cenário não corresponde à versão do orçamento (Orçado exige a versão original; Reforecast exige uma revisão).',
      scenarioPeriodGrain: 'O calendário do orçamento não é mensal; as colunas do DRE são mensais.',
      scenarioMultiCurrency:
        'A versão do orçamento mistura moedas entre as linhas — valores nunca são somados entre moedas. Ajuste o orçamento para uma única moeda.',
      scenarioFxRateUnresolved:
        'Não foi possível obter a cotação PTAX de {{from}} para {{to}}, então a coluna projetada foi recusada em vez de exibir um valor estimado ou omitir as linhas em {{from}}. Tente novamente mais tarde.',
      scenarioProjectionCurrencyDivergence:
        'As metades de receita e despesa da projeção resolveram moedas funcionais diferentes para o mesmo escopo. Verifique a moeda funcional configurada nos livros contábeis das entidades selecionadas.',
      scenarioProjectionRateDivergence:
        'As metades de receita e despesa da projeção converteram {{from}} por boletins PTAX diferentes na mesma coluna. A coluna foi recusada para não publicar totais convertidos por taxas distintas — reavalie o relatório.',
      scenarioInvalidParams:
        'Parâmetros inválidos para o cenário de receita projetada (renovações assumidas entre 0 e 10).',
      scenarioUnsupported: 'Este template não suporta o cenário selecionado.',
      scenarioCellCollision:
        'Receita e despesa projetadas atribuíram o mesmo valor à mesma linha — a coluna foi recusada para não escolher um lado em silêncio.',
      scenarioForecastRuleSetRequired:
        'Projeções precisa de um conjunto de regras de previsão. Cadastre um via agente ou escolha um conjunto existente.',
      bookScopeEntityNotGoverned:
        'O livro selecionado não governa todas as entidades do escopo. Ajuste a seleção de entidades ou o livro.',
      bookScopeAccountOutsideCoa:
        'A projeção referencia contas fora do plano de contas do livro selecionado.',
      bookScopeUnsupported:
        'O escopo do livro não suporta esta projeção. Ajuste a seleção de entidades ou o livro.',
      assumptionUnresolved:
        'A fórmula aponta para uma Premissa que não existe neste cenário. Cadastre a Premissa ou ajuste a fórmula.',
      encargosJurisdiction:
        'Encargos brasileiros não se aplicam a esta entidade. Remova-a da seleção ou use outra entidade.',
      encargosEntityPolicyNotEffective:
        'Esta entidade tem uma política de encargos própria, mas nenhuma vigência cobre o período solicitado. Configure a taxa para o período ou ajuste o período do relatório.',
      scenarioCurrencyUnresolved:
        'Não foi possível determinar a moeda deste cenário. Verifique o livro das entidades selecionadas.',
      periodsLimit:
        'O intervalo tem mais meses do que o relatório comporta. Encurte o período (máximo {{limit}} meses).',
      drilldownScenarioUnsupported:
        'Esta célula de cenário não tem composição detalhada. Abra uma célula de linha agregada no Realizado ou no orçamento.',
      drilldownFxPinMissing:
        'A composição desta célula usa exatamente as cotações com que ela foi calculada. O escopo mudou desde então e a célula não pode mais ser detalhada por essas cotações — reavalie o relatório e abra a célula novamente.',
      drilldownFxPinPairMissing:
        'Esta célula foi calculada sem valores em {{from}}, mas a composição agora encontra linhas nessa moeda. Convertê-las pela cotação de hoje somaria um valor que a célula não contém — reavalie o relatório para incluí-las.',
      drilldownFxPinTargetChanged:
        'A moeda funcional do escopo mudou desde que esta célula foi calculada, então as cotações fixadas nela apontam para outra moeda. Reavalie o relatório e abra a célula novamente.',
      drilldownHandleInvalid:
        'Este relatório ficou desatualizado em relação ao servidor — reavalie o relatório e tente o detalhamento novamente.',
      forecastFilterInvalid:
        'O filtro da regra de previsão aponta para um registro inválido. Corrija o filtro no conjunto de regras.',
      payrollRunRateSourceMissing:
        'A regra de folha está ativa, mas a fonte da folha não foi carregada. Recarregue o relatório ou remova a regra.',
      payrollRunRateDuplicate:
        'O conjunto de regras só pode ter uma regra de folha (PAYROLL_RUNRATE).',
      payrollRunRateHeadcountCoactive:
        'Folha modelada e Plano de Contratações não podem ficar ativos no mesmo conjunto — isso contaria o pessoal duas vezes.',
      closedPeriod:
        'Esse mês já está fechado ou congelado em um snapshot. Para alterar, peça uma reapresentação com justificativa.',
      overridePopulationUndetermined:
        'Não foi possível determinar as contas desta linha para aplicar o ajuste. Use uma linha com contas explícitas.',
      overrideRollupKindInvalid: 'Não é possível fixar um subtotal. Ajuste uma linha de detalhe.',
      overrideCurrencyMismatch:
        'O ajuste está em moeda diferente da do relatório. Recrie o ajuste na moeda do livro.',
      snapshotDrilldownUnavailable:
        'Este snapshot não tem uma composição congelada para esta célula — ela foi capturada antes desse recurso existir, ou não pôde ser reconciliada com o valor congelado no momento da captura.',
    },
    scenarioColumn: {
      label: 'Cenários',
      placeholder: 'Selecionar orçamento',
      templateDefaults: 'Colunas do modelo',
      none: 'Somente Realizado',
      ruleSet: 'Conjunto de regras',
      ruleSetPlaceholder: 'Selecionar conjunto de regras',
      projectionsNeedRuleSet: 'Projeções exige um conjunto de regras ativo.',
      projectionsManageRuleSets: 'Criar ou ativar conjunto',
    },
    displayOptions: {
      trigger: 'Opções de visualização',
      amountScale: {
        label: 'Granularidade dos valores',
        FULL: 'Números completos',
        THOUSANDS: 'Milhares (k)',
        MILLIONS: 'Milhões (M)',
      },
      percentDecimals: {
        label: 'Precisão de percentuais',
        decimals0: 'Sem casas decimais',
        decimals1: '1 casa decimal',
        decimals2: '2 casas decimais',
      },
      signColors: 'Colorir positivos/negativos',
      showCurrency: 'Exibir moeda',
      showCents: 'Exibir centavos',
      showNaoClassificado: 'Exibir Não Classificado',
      showNaoClassificadoHint:
        'Linha de reconciliação com o valor que não pôde ser atribuído a uma linha da DRE. Não entra em nenhum total — ocultá-la não altera nenhum outro número.',
      saveError: 'Erro ao salvar as opções de visualização.',
    },
    coverage: {
      title: 'Cobertura da projeção',
      explainer:
        'Ressalvas desta coluna: itens omitidos ou projetados parcialmente e conversões de moeda aplicadas.',
      truncatedCount: '{{shown}} de {{total}}',
      truncatedTitle: 'Cobertura da projeção — {{shown}} de {{total}} ressalvas',
      truncatedNote:
        'A lista foi limitada a {{shown}} das {{total}} ressalvas desta coluna. As divulgações — conversões de moeda, fixações manuais e premissas — vêm primeiro, então nenhuma delas ficou de fora.',
      summarizedCount: '{{total}} ({{reasons}} motivos)',
      summarizedTitle: 'Cobertura da projeção — {{total}} ressalvas em {{reasons}} motivos',
      summarizedNote:
        'As ressalvas em massa desta coluna foram agrupadas por motivo em vez de listadas uma a uma. As divulgações — conversões de moeda, fixações manuais e premissas — continuam listadas individualmente acima.',
      summaryGroupTitle: '{{count}} — {{reason}}',
      summarySampleNote: 'mostrando {{shown}} de {{total}}',
      partial: 'parcial',
      fx: {
        badge: 'convertido',
        rate: '1 {{from}} = {{rate}} {{to}}',
        quoteDate: 'Boletim PTAX de {{date}} ({{provider}}), aplicado a todos os meses projetados',
        convertedCount_one: '{{count}} linha convertida',
        convertedCount_other: '{{count}} linhas convertidas',
      },
      relativeRange: {
        resolvedAnchor: 'Referência resolvida em {{month}}',
      },
      cancelledWindows_one: '{{count}} janela de serviço cancelada suprimida',
      cancelledWindows_other: '{{count}} janelas de serviço canceladas suprimidas',
      reasons: {
        NO_CLOSED_PERIOD:
          'Nenhum período contábil foi fechado; a faixa usou o mês-calendário anterior',
        CONVERTED_AT_PTAX:
          'Valores convertidos para a moeda funcional do escopo pela cotação PTAX de venda',
        NO_START_DATE: 'Sem data de início',
        NO_TERM_END_DATE: 'Contrato sem termo final',
        NO_EFFECTIVE_VERSION: 'Sem versão vigente',
        NO_PROJECTABLE_ITEMS: 'Sem itens projetáveis',
        UNATTRIBUTABLE: 'Sem conta para atribuir a uma linha da DRE',
        FILTER_REFERENCE_UNRESOLVED: 'O filtro aponta para um registro que não existe mais',
        PAYROLL_HISTORICAL_RUN_RATE: 'Folha projetada pela média histórica, não modelada',
        UNPOSTED_BEFORE_RANGE: 'Obrigações não lançadas em competências anteriores ao período',
        NO_COMPETENCY_DATE: 'Sem data de competência',
        NO_AMOUNT: 'Sem valor definido',
        OCCURRENCE_CAP_REACHED: 'Limite de ocorrências projetadas atingido',
        ENDED_SOURCE_IN_RESIDUAL:
          'Fonte encerrada — histórico somado à média do resíduo, não à própria linha',
        NO_PROJECTABLE_RULES: 'O conjunto de regras não tem nenhuma regra ativa',
        PAYROLL_EXCLUDED_BY_HEADCOUNT_PLAN:
          'Folha excluída do residual — o Plano de Contratações projeta o pessoal',
        HEADCOUNT_PLAN_UNATTRIBUTED:
          'Posições sem conta gerencial — o valor foi para Não Classificado; cadastre a conta na Posição',
        ENCARGOS_JURISDICTION_UNSUPPORTED:
          'Encargos BR não se aplicam a esta entidade — pessoal omitido da projeção',
        ENCARGOS_POLICY_NOT_EFFECTIVE:
          'Esta entidade tem uma política de encargos própria, mas nenhuma vigência cobre o período — pessoal omitido da projeção',
        PAYROLL_ROSTER: 'Quadro da última folha mensal, sem rescisões',
        JOINERS_NOT_MODELLED: 'Admissões futuras não são derivadas da folha',
        PAYROLL_PROVISION_BRANCH:
          'Provisões seguem o ramo do tenant (importadas ou calculadas na folha)',
        UNRECOGNISED_RUBRIC: 'Rubricas não reconhecidas entram na base como vencimento',
        PAYROLL_MANAGERIAL_UNRESOLVED:
          'Conta gerencial da folha não resolvida — a linha ficou na Fonte CONTABIL',
        PAYROLL_FILTER_EXCLUDED:
          'Funcionário fora do recorte do driver de folha (centro de custo, projeto, departamento ou entidade).',
        UNMAPPED_COLLABORATOR: 'Colaborador PJ sem vínculo de party ou fornecedor',
        PAYROLL_EXCLUDED_BY_PAYROLL_RUNRATE:
          'Folha e PJ mapeados saíram do residual — a variante modela o pessoal',
        PAYROLL_BASE_RUN_STALE:
          'A última folha mensal desta entidade é anterior à janela — a projeção foi recusada',
        PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS:
          'Há rescisão sem CPF — o colaborador não foi projetado para não apagar um reingresso',
        PAYROLL_CURRENCY_MISMATCH:
          'A folha ou a nota do colaborador PJ está em moeda diferente da funcional do livro — não convertida',
        PAYROLL_PROVISION_MISSING:
          'Não publicável: provisão importada ausente para integrantes desta entidade — a emissão foi recusada',
        INSUFFICIENT_HISTORY:
          'Histórico de RPA insuficiente para a janela e recorrência configuradas.',
        UNAVAILABLE:
          'Folha CLT indisponível. Importe uma folha mensal para projetar este componente.',
        NO_SOURCE_DATA: 'Não há folha importada do tipo habilitado na janela configurada.',
        PAYROLL_EMPLOYEE_SKIPPED: 'Colaboradores com valor zero ou negativo foram omitidos',
        PAYROLL_COLLABORATOR_STALE:
          'Colaborador PJ sem nota na janela recente — a taxa não é projetada para sempre',
        PAYROLL_COLLABORATOR_NO_EMISSION:
          'Colaborador PJ sem ocorrência projetável — o template já está materializado ou não cabe na janela',
        PAYROLL_REFUSED_COVERED_BY_RESIDUAL:
          'A emissão de folha desta entidade foi recusada — o residual ad-hoc cobre o histórico postado',
        PAYROLL_TEMPLATE_NOT_MODELLED:
          'Template de folha sem cobertura — nenhuma variante ativa está projetando este contrato PJ',
        PAYROLL_TEMPLATE_WINDOW_DRIFTED:
          'Vigência do template de folha foi alterada depois do documento — classificação mantida, não recalculada',
        UNRESOLVED: 'Premissa não encontrada neste cenário',
        REALIZADO_HAS_NO_PREMISSA: 'O Realizado não usa Premissas — a célula ficou em branco',
        SCENARIO_KIND_INCONSISTENT: 'Esta Premissa pertence a outro tipo de cenário',
        CURRENCY_MISMATCH: 'Ajuste em outra moeda — não foi somado',
        AGGREGATION_PIN_SKIPPED: 'Subtotal não aceita valor fixo — ajuste a linha de detalhe',
        CALCULATED_PIN_SKIPPED: 'Linha calculada: o valor fixo não entra nos subtotais',
        NULL_CELL_SKIPPED: 'Não havia valor neste mês — o ajuste não cria um número do zero',
        PIN_NOT_CONTAINED: 'Este ajuste não entra em nenhum subtotal da DRE',
        PIN_ACCOUNTS_UNMATERIALIZED:
          'As contas deste ajuste ainda não estão no plano de contas do período',
        OPERAND_NOT_ADJUSTED:
          'Uma parte da fórmula não recebeu o ajuste para não contar duas vezes',
        OPERANDS_ONLY: 'O ajuste ficou só nas partes da fórmula, sem entrar no total',
        PRIOR_PERIOD_OUTSIDE_COLUMN: 'A fórmula precisa de um mês que esta coluna não mostra',
      },
      flags: {
        NO_CLOSED_PERIOD_FALLBACK: 'Referência provisória até existir o primeiro período fechado',
        USAGE_EXCLUDED: 'Receita de uso não é projetada',
        PAYROLL_RUN_RATE: 'Folha na média histórica do residual',
        UNSTAMPED_PAYROLL_JOURNAL:
          'Lançamento de folha sem centro de custo/departamento — coberto pela entidade',
        UNATTRIBUTED: 'Sem conta para atribuir a uma linha da DRE',
        INCLUDES_BOOK_REMEASUREMENT:
          'Inclui remensuração cambial sobre o valor contábil (PTAX dinâmica)',
      },
    },
    templateError: 'Erro ao carregar o template.',
    retry: 'Tentar novamente',
    notFound: 'Template não encontrado.',
    invalidRange: 'O período inicial deve ser igual ou anterior ao final.',
    tooManyPeriods: 'Intervalo máximo de {{max}} meses.',
    noEntities: 'Selecione ao menos uma entidade para avaliar o template.',
    emptyValuesNotice:
      'Este template possui apenas linhas estruturais (cabeçalhos e espaçadores) — adicione linhas de rollup para ver valores.',
    noRows: 'Este template não possui linhas.',
    drilldown: {
      // ALC-2608: em as-of o detalhamento decompõe dados ATUAIS — o total pode
      // não conciliar com a célula congelada. Até existir uma composição
      // armazenada, o clique é recusado em vez de avisado.
      disabledAsOf:
        'O detalhamento mostra a composição ao vivo — indisponível em um snapshot até que a trilha congelada exista.',
    },
    buckets: {
      naoClassificado: 'Não Classificado',
      semEventoDeCaixa: 'Sem Evento de Caixa',
      naoClassificadoHint:
        'Valores que a Fonte deste template não consegue atribuir a nenhum rollup.',
      semEventoDeCaixaHint:
        'Valores de competência sem liquidação de caixa rastreável neste template de Regime Caixa.',
    },
  },
  entitySelect: {
    label: 'Entidades',
    placeholder: 'Selecione entidades',
    allSelected: 'Todas as entidades',
    selected_one: '{{count}} entidade',
    selected_other: '{{count}} entidades',
    selectAll: 'Selecionar todas',
    clear: 'Limpar seleção',
    error: 'Erro ao carregar entidades.',
    empty: 'Nenhuma entidade disponível.',
  },
  drilldown: {
    title: 'Composição da célula',
    description: 'Linhas de origem que compõem o valor desta célula.',
    period: 'Período',
    scenario: 'Cenário',
    entities: 'Entidades',
    entitiesCount_one: '{{count}} entidade',
    entitiesCount_other: '{{count}} entidades (combinado, sem eliminações)',
    cellValue: 'Valor da célula',
    reconcilingTotal: 'Total reconciliado',
    roundingDifference: 'Diferença de arredondamento',
    loading: 'Carregando composição…',
    error: 'Erro ao carregar a composição.',
    emptyRows: 'Nenhuma linha de origem para este valor.',
    // ALC-2608: banner shown instead of `description` when the drawer serves a
    // snapshot's FROZEN composition rather than a live fetch.
    frozenComposition: 'Composição congelada em {{date}}.',
    frozenTruncated: 'Mostrando {{shown}} de {{total}} linhas.',
    columns: {
      type: 'Origem',
      description: 'Descrição',
      dateUsed: 'Data utilizada',
      amount: 'Valor',
    },
    rowTypes: {
      journal_line: 'Lançamento contábil',
      classification_split: 'Classificação gerencial',
      settlement_allocation: 'Liquidação de caixa',
      budget_line: 'Linha de orçamento',
      metric_point: 'Ponto de métrica',
      formula_operand: 'Operando da fórmula',
      projected_revenue: 'Receita projetada',
      projected_expense: 'Despesa projetada',
      projected_revenue_fx_remeasurement: 'Remensuração PTAX',
    },
    provenance: {
      RECOGNIZED: 'Reconhecida',
      SCHEDULED: 'Agendada',
      PROJECTED_TERM: 'Projetada (contrato)',
      PROJECTED_RENEWAL: 'Projetada (renovação)',
      // Despesa projetada (ALC-2116): compromisso vs. premissa. Só
      // EXISTING_DOCUMENT é um compromisso já registrado.
      EXISTING_DOCUMENT: 'Documento existente',
      RECURRING_OCCURRENCE: 'Ocorrência recorrente',
      HISTORICAL_AVERAGE: 'Média histórica',
      HEADCOUNT_PLAN: 'Plano de Contratações',
      PAYROLL_RUNRATE: 'Folha (run-rate)',
    },
    /** Fallback quando a linha projetada não traz rótulo nem documento. */
    sourceKinds: {
      BILL: 'Conta a pagar',
      PURCHASE_ORDER: 'Ordem de compra',
      RECURRING_BILL_TEMPLATE: 'Modelo recorrente',
      ADHOC_RESIDUAL: 'Residual ad-hoc',
      HEADCOUNT_POSITION: 'Posição',
      PAYROLL_EMPLOYEE: 'Colaborador CLT',
      PAYROLL_COLLABORATOR: 'Colaborador PJ',
    },
    dateSources: {
      JOURNAL: 'Data do lançamento',
      BILL_MANAGERIAL: 'Data gerencial da conta',
      BILL_ISSUE: 'Data de emissão da conta',
      INVOICE_MANAGERIAL: 'Data gerencial da fatura',
      INVOICE_ISSUE: 'Data de emissão da fatura',
    },
    frozenCellValue: 'Valor congelado',
  },
  headcount: {
    title: 'Plano de Contratações',
    description:
      'Cabeças e custo de pessoal por mês e departamento — o plano para meses futuros, a folha para os realizados. Pessoas sem Posição aparecem em Não planejado.',
    entity: 'Entidade',
    from: 'De',
    to: 'Até',
    breadcrumbFpa: 'FP&A',
    department: 'Departamento',
    departmentOptional: 'Departamento (opcional)',
    noDepartment: 'Sem departamento',
    unplanned: 'Não planejado',
    heads: '{{count}} HC',
    visualization: 'Visualização',
    visualizationOptions: {
      costAndHeadcount: 'Custo + HC',
      costOnly: 'Apenas custo',
      headcountOnly: 'Apenas headcount',
    },
    empty: 'Nenhuma Posição no intervalo.',
    legend:
      '— em um departamento = posição ainda não iniciada ou fora do período. Na Equipe atual: — = mês já realizado (resultado da folha) ou nenhuma folha anterior para carregar.',
    inactiveCell: 'Posição ainda não iniciada ou fora do período',
    encargosHint:
      'Custo mensal = salário × {{multiplier}} (encargos estatutários {{rate}}% + provisões de 13º/férias).',
    encargosPolicyDefault: 'política padrão do tenant',
    encargosPolicyConfigured: 'taxas configuradas do tenant',
    currentTeamUnplannedBadge: 'não coberta pelo plano no mês',
    currentTeamHeaderTooltip:
      'Carry-forward da última folha postada anterior a cada mês, na base de custo empregador do plano — salário mais encargos e provisões de 13º/férias, por isso o valor fica acima do bruto da folha. Só entra quem é CLT; colaboradores PJ aparecem em uma linha própria. Não é dado planejado — sem desligamentos ou reajustes.',
    currentTeamTooltip:
      'Carry-forward da folha postada em {{period}}. Não é dado planejado — sem desligamentos ou reajustes.',
    currentTeamEmpty: 'Nenhuma folha mensal postada para carregar a equipe atual.',
    realizedMonthDash:
      'Mês já realizado — a equipe atual entra pelo resultado da folha, não por carry-forward.',
    subtotalPlanned: 'Planejado',
    subtotalCurrentTeam: 'Equipe atual (CLT)',
    subtotalCurrentTeamPj: 'Equipe atual (PJ)',
    currentTeamPjHeaderTooltip:
      'Contas recorrentes ativas de fornecedores colaboradores com a natureza Folha de pagamento. O valor é o contratado, sem encargos CLT, e só aparece dentro das datas de início e fim da recorrência.',
    currentTeamPjTooltip: 'Valor contratado na conta recorrente de Folha de pagamento.',
    subtotalGrandTotal: 'Total geral',
    end: 'Fim',
    endOptional: 'Fim (opcional)',
    openEnded: 'Sem término',
    error: 'Erro ao carregar o plano de contratações.',
    positions: 'Posições',
    positionTitle: 'Título',
    start: 'Início',
    salary: 'Salário',
    status: 'Status',
    statusValues: {
      PLANNED: 'Planejada',
      HIRED: 'Contratada',
      CANCELLED: 'Cancelada',
    },
    addPosition: 'Adicionar Posição',
    edit: 'Editar',
    editDialogTitle: 'Editar Posição',
    editDialogDescription:
      'Corrige título, salário, departamento ou vigência da Posição planejada. Contratação e cancelamento continuam nas ações da linha.',
    savePosition: 'Salvar Posição',
    cancelEdit: 'Cancelar',
    cancel: 'Cancelar',
    cancelDialogTitle: 'Cancelar Posição',
    cancelDialogDescription:
      'Esta ação cancela a Posição "{{title}}". Ela deixa de aparecer no plano de contratações e essa ação não pode ser desfeita.',
    cancelDialogConfirm: 'Cancelar Posição',
    discardDialogTitle: 'Descartar Posição não salva?',
    discardDialogDescription:
      'Você tem alterações não salvas nesta Posição. Se fechar agora, elas serão perdidas.',
    discardDialogConfirm: 'Descartar',
    suggestions: 'Sugestões de contratação',
    employee: 'Colaborador',
    employeeOrContract: 'Colaborador/Contrato',
    contractValue: 'Valor',
    contractValueDelta: '{{delta}} vs. plano',
    score: 'Score',
    confirmHire: 'Confirmar contratação',
    linkAndHire: 'Vincular e contratar',
    changeLink: 'Alterar vínculo',
    changeLinkDialogTitle: 'Alterar vínculo do contrato',
    changeLinkDialogDescription:
      'Escolha outro fornecedor e conta recorrente de Folha de pagamento para vincular a esta Posição contratada.',
    currentContract: 'Contrato atual',
    currentContractUnknown: 'Contrato vinculado',
    contractEnded: 'Contrato encerrado',
    changeLinkVendorLabel: 'Fornecedor',
    changeLinkContractLabel: 'Conta recorrente',
    changeLinkConfirm: 'Confirmar vínculo',
    mutationError: 'Não foi possível salvar a Posição.',
    confirmHireError: 'Não foi possível confirmar a contratação.',
    retry: 'Tentar novamente',
    toast: {
      createSuccess: 'Posição criada com sucesso.',
      updateSuccess: 'Posição atualizada com sucesso.',
      cancelSuccess: 'Posição cancelada com sucesso.',
      confirmHireSuccess: 'Contratação confirmada com sucesso.',
      linkAndHireSuccess: 'Contrato vinculado e Posição contratada com sucesso.',
      relinkSuccess: 'Vínculo do contrato atualizado com sucesso.',
    },
    errors: {
      salaryInvalid:
        'O salário precisa ser um valor positivo. Use 1500,00 — valores negativos ou vazios não são aceitos.',
      endBeforeStart: 'O mês final precisa ser igual ou posterior ao mês inicial.',
      titleInvalid: 'Informe um título para a Posição.',
      periodInvalid: 'Use o formato AAAA-MM, por exemplo 2026-03.',
      hireConfirmInvalid:
        'Não foi possível confirmar a contratação. A Posição precisa estar planejada e o colaborador disponível.',
      planExists: 'Já existe um Plano de Contratações para esta entidade. Abra o plano existente.',
      refOutOfScope: 'Essa Posição ou colaborador não pertence a esta entidade.',
      refInactive:
        'O departamento ou centro de custo selecionado está inativo. Escolha um ativo e tente novamente.',
      currencyMismatch:
        'O salário está em moeda diferente da do livro desta entidade. Ajuste a moeda ou a entidade.',
      multiCurrencyScope:
        'As entidades selecionadas usam moedas diferentes, então não existe um total único. Selecione uma entidade para ver os valores.',
      pjFrequencyUnsupported:
        'Uma conta recorrente de Folha de pagamento precisa ter recorrência Mensal. Altere a recorrência para Mensal.',
      currencyUnresolved:
        'Não foi possível determinar a moeda do plano. Verifique se a entidade tem um livro atribuído.',
      salaryRaiseInvalid:
        'O reajuste é uma fração: use 0,05 para 5%. Números inteiros como 2 ou 5 são lidos como 200%/500% e não são aceitos; a taxa deve ser maior que −1 e menor que 10.',
      periodsLimit:
        'O intervalo tem mais meses do que o relatório comporta. Encurte o período (máximo {{limit}} meses).',
      encargosJurisdiction:
        'Encargos brasileiros não se aplicam a esta entidade. Remova-a da seleção ou use outra entidade.',
      encargosEntityPolicyNotEffective:
        'Esta entidade tem uma política de encargos própria, mas nenhuma vigência cobre o período solicitado. Configure a taxa para o período ou ajuste o período do relatório.',
      scenarioCurrencyUnresolved:
        'Não foi possível determinar a moeda deste cenário. Verifique o livro das entidades selecionadas.',
      foreignKey:
        'O registro referenciado não existe ou está fora do escopo. Verifique a entidade ou o colaborador.',
      uniqueConstraint:
        'Já existe um registro com esses dados. Abra o existente em vez de criar outro.',
      pjLinkTypeMismatch:
        'Posições PJ contratam vinculando uma conta recorrente; posições CLT contratam vinculando um colaborador. Verifique o tipo de contratação da Posição.',
      pjLinkContractInvalid:
        'Essa conta recorrente não é uma conta de Folha de pagamento ativa nesta entidade. Escolha outra conta recorrente.',
      pjLinkVigenciaMismatch:
        'A vigência dessa conta recorrente não cobre o mês de início da Posição. Ajuste a vigência ou escolha outro contrato.',
      pjLinkAlreadyLinked:
        'Essa conta recorrente já está vinculada a outra Posição. Cada contrato pode ser vinculado a apenas uma Posição.',
    },
  },
  assumptions: {
    errors: {
      generic: 'Não foi possível salvar a Premissa.',
      valueInvalid: 'O valor da Premissa precisa ser um número decimal. Exemplo: 0,05 para 5%.',
      valueInvalidScale:
        'O valor da Premissa aceita no máximo 6 casas decimais. Exemplo: 0,05 para 5%.',
      valueInvalidMagnitude:
        'Esse valor é grande demais para a Premissa. Use um número com menos de 14 dígitos inteiros.',
      keyInvalid:
        'A chave da Premissa usa só letras minúsculas, números e underscore. Exemplo: churn_pct.',
      keyTaken:
        'Já existe uma Premissa com essa chave neste cenário. Escolha outra chave ou edite a existente.',
      nameInvalid: 'Informe um nome para a Premissa.',
      unitInvalid: 'Informe a unidade da Premissa (por exemplo taxa, reais ou pessoas).',
      scenarioRequired: 'Vincule a Premissa a um orçamento ou a um conjunto de regras.',
      scenarioAmbiguous:
        'A Premissa não pode pertencer a um orçamento e a um conjunto de regras ao mesmo tempo. Escolha um dos dois.',
      inUse:
        'Esta Premissa está usada em uma fórmula. Remova a referência da fórmula antes de excluir.',
      unresolved:
        'A fórmula aponta para uma Premissa que não existe neste cenário. Cadastre a Premissa ou ajuste a fórmula.',
      closedPeriod:
        'Esse mês já está fechado ou congelado em um snapshot. Para alterar, peça uma reapresentação com justificativa.',
      overrideAmountInvalid: 'O valor do ajuste precisa ser um número decimal.',
      overrideAmountInvalidScale: 'O valor do ajuste aceita no máximo 6 casas decimais.',
      overrideAmountInvalidMagnitude:
        'Esse valor é grande demais para o ajuste. Use um número com menos de 14 dígitos inteiros.',
      overrideCurrencyMismatch:
        'O ajuste está em moeda diferente da do relatório. Recrie o ajuste na moeda do livro.',
      overrideOverlap:
        'Já existe um ajuste para esta linha no mesmo período. Altere o intervalo ou remova o ajuste anterior.',
      overrideRangeInvalid: 'O mês final do ajuste precisa ser igual ou posterior ao inicial.',
      overrideScopeRequired: 'Informe a entidade e o livro do ajuste.',
      overrideRollupInvalid: 'A linha escolhida não existe ou foi arquivada. Escolha outra linha.',
      overrideRollupKindInvalid: 'Não é possível fixar um subtotal. Ajuste uma linha de detalhe.',
      overridePopulationUndetermined:
        'Não foi possível determinar as contas desta linha para aplicar o ajuste. Use uma linha com contas explícitas.',
      forecastFilterInvalid:
        'O filtro da regra de previsão aponta para um registro inválido. Corrija o filtro no conjunto de regras.',
      foreignKey:
        'O registro referenciado não existe ou está fora do escopo. Verifique a entidade, a linha ou o colaborador.',
      uniqueConstraint:
        'Já existe um registro com esses dados. Abra o existente em vez de criar outro.',
    },
  },
  derivedLimits: {
    title: 'Limites derivados',
    description:
      'A regra é só metadado. Aprovar grava o valor derivado como limite estático do período.',
    fallbackLine:
      'A linha Outros (fallback) não aceita regra de derivação. Use uma linha classificável.',
    formula: {
      label: 'Regra de derivação',
      empty: 'Nenhuma regra nesta linha. Monte uma fórmula com Premissas e Métricas.',
      readOnly: 'Esta fórmula não pode ser editada neste editor. Grave de novo para substituir.',
      addAssumption: 'Premissa',
      addMetric: 'Métrica',
      addNumber: 'Número',
      inputs: 'Premissas / Métricas',
      operators: 'Operadores',
      numberPlaceholder: 'Valor',
      pickAssumption: 'Escolher Premissa',
      pickMetric: 'Escolher Métrica',
      noAssumptions: 'Nenhuma Premissa neste orçamento.',
      noMetrics: 'Nenhuma Métrica disponível.',
      save: 'Salvar regra',
      clear: 'Remover regra',
      saved: 'Regra de derivação salva.',
      cleared: 'Regra de derivação removida.',
      invalid: 'A fórmula está incompleta. Termine a expressão antes de salvar.',
      removeToken: 'Remover token',
    },
    preview: {
      title: 'Derivado vs limite atual',
      period: 'Período',
      current: 'Atual',
      derived: 'Derivado',
      currentLimit: 'Limite Atual',
      derivedLimit: 'Limite Derivado',
      inputs: 'Métrica / Premissas',
      status: 'Status',
      empty: 'Não há períodos para revisar neste intervalo.',
      unresolved: 'Sem Premissa/Métrica',
      inputsToggle: 'Ver Premissas e Métricas',
      inputsHide: 'Ocultar Premissas e Métricas',
      assumption: 'Premissa',
      metric: 'Métrica',
      inputsEmpty: 'Nenhuma Premissa ou Métrica resolvida neste período.',
    },
    annotations: {
      PARENT_SUM_EXCEEDS:
        'A soma das linhas filhas ({{childrenSum}}) já excede o limite do pai que será gravado ({{parentAmount}}).',
    },
    status: {
      drifted: 'Pendente',
      inSync: 'Alinhado',
      unresolved: 'Não resolvido',
      unstampable: 'não estampável: valor zero/negativo',
    },
    approve: {
      action: 'Aprovar limites derivados',
      none: 'Não há períodos resolvidos com diferença para aprovar.',
      selfApproval: 'Quem cadastrou a regra não pode aprovar o próprio limite.',
      success: 'Limites derivados gravados na linha.',
      loadError: 'Não foi possível carregar a regra ou a prévia.',
      unstampableHint:
        'Há períodos com diferença, mas o valor derivado é zero ou negativo e não pode ser gravado.',
      partialFailure:
        'Os períodos {{stamped}} foram gravados. Os períodos {{failed}} falharam: {{reason}}',
    },
    confirm: {
      title: 'Aprovar limites derivados',
      description:
        'Confira o limite atual e o valor derivado por período. A aprovação grava exatamente o valor que você está vendo.',
      confirm: 'Aprovar e gravar',
      cancel: 'Voltar',
      period: 'Período',
      current: 'Atual',
      derived: 'Novo',
      none: '—',
    },
    queue: {
      title: 'Aprovações pendentes',
      description: 'Linhas deste orçamento com limite derivado diferente do valor vigente.',
      open: 'Fila de limites derivados',
      empty: 'Nenhuma linha com limite derivado pendente de aprovação.',
      driftedCount_one: '{{count}} período pendente',
      driftedCount_other: '{{count}} períodos pendentes',
      openLine: 'Abrir linha',
      lineError: 'Não foi possível carregar a prévia desta linha.',
    },
    errors: {
      formulaInvalid:
        'A fórmula de derivação é inválida. Use apenas Premissas e Métricas (sem rollups), sem chaves extras, dentro do limite de tamanho.',
      amountInvalid:
        'O valor derivado não cabe em DECIMAL(18,2). Ajuste a fórmula ou as Premissas para no máximo duas casas decimais.',
      zeroNotStampable:
        'Um limite derivado de 0,00 não pode ser gravado: o modelo atual trata zero como “sem limite” e volta para o valor da linha. Use um valor positivo.',
      expectedMismatch:
        'O valor derivado mudou desde a revisão. Recarregue a sugestão e aprove de novo o valor que você está vendo.',
      notActive:
        'Carimbe limites no orçamento original em preparação ou no orçamento em vigor. Reforecasts precisam ser aprovados antes do carimbo.',
      noRule: 'Cadastre uma regra de derivação nesta linha antes de aprovar um limite.',
      unresolved:
        'Há períodos sem Premissa ou Métrica resolvida. O intervalo inteiro foi recusado; veja os períodos em details.',
      periodOutsideLine: 'Os períodos pedidos estão fora da janela da linha orçamentária.',
      rangeInverted: 'A data inicial deve ser anterior ou igual à data final.',
      lineOwnerForbidden:
        'Só o dono do orçamento ou um responsável autorizado da linha pode carimbar o limite.',
      lineOwnerLimit: 'O total após o carimbo ultrapassa o teto permitido para este responsável.',
      budgetArchived: 'Orçamentos arquivados não aceitam regra nem carimbo.',
      fallbackLine:
        'A linha Outros (fallback) não aceita regra de derivação. Use uma linha classificável.',
      closedPeriod:
        'Períodos fechados ou já reportados são imutáveis por esta API. Use o fluxo de retificação.',
      budgetLineReadRequired:
        'Ver o limite aprovado exige a permissão budget:line:read além de fpa:read.',
      currencyMismatch:
        'Um operando está em outra moeda que a linha. Limites derivados nunca convertem — ajuste a Métrica ou a Premissa.',
      periodsMissing:
        'Falta período no calendário da linha. O intervalo inteiro foi recusado; veja os períodos em details.',
      selfApproval:
        'A aprovação exige um segundo usuário. Quem cadastrou a regra atual não pode carimbar o limite.',
      ruleAuthorRequired:
        'Grave a regra de derivação novamente antes de aprovar. Esta cópia não tem autor registrado.',
      stampedPeriodConflict:
        'Esta alteração derrubaria um período carimbado. Envie dropStampedPeriodIds com cada período a remover.',
    },
  },
  forecastRuleSets: {
    title: 'Conjuntos de regras de previsão',
    description:
      'As regras que a coluna Projeções usa: contas recorrentes, documentos em aberto, residual ad hoc, plano de headcount e folha (run-rate).',
    manage: 'Gerenciar conjuntos de regras',
    newSet: 'Novo conjunto',
    empty: 'Nenhum conjunto de regras. Crie o primeiro para habilitar a coluna Projeções.',
    emptyEditor: 'Selecione um conjunto ou crie um novo.',
    name: 'Nome',
    descriptionLabel: 'Descrição',
    status: 'Status',
    statusValues: {
      ACTIVE: 'Ativo',
      ARCHIVED: 'Arquivado',
    },
    changeReason: 'Motivo da alteração',
    setDetails: 'Conjunto',
    accountsEntity: 'Entidade (contas contábeis)',
    selectEntityPlaceholder: 'Selecione a entidade',
    selectEntityForAccounts: 'Selecione uma entidade para ver as contas contábeis.',
    changelog: {
      open: 'Ver histórico',
      title: 'Histórico de alterações',
      description: 'Alterações salvas neste conjunto de regras.',
      emptyTitle: 'Nenhuma alteração registrada',
      emptyDescription: 'O histórico aparece depois que o conjunto é salvo.',
      loadError: 'Erro ao carregar o histórico de alterações.',
      created: 'Conjunto criado',
      updated: 'Conjunto atualizado',
      deleted: 'Conjunto excluído',
      noReason: 'Sem motivo informado',
    },
    rules: 'Regras',
    addRule: 'Adicionar regra',
    removeRule: 'Remover regra',
    remove: 'Remover',
    removeRuleConfirmTitle: 'Remover esta regra?',
    removeRuleConfirm:
      'A regra será removida deste rascunho. Salve o conjunto para aplicar a alteração.',
    ruleKind: 'Tipo',
    ruleStatus: 'Regra ativa',
    kindValues: {
      RECURRING_BILLS: 'Contas recorrentes',
      OPEN_DOCUMENTS: 'Documentos em aberto',
      ADHOC_RESIDUAL: 'Residual ad hoc',
      HEADCOUNT_PLAN: 'Plano de headcount',
      PAYROLL_RUNRATE: 'Folha (run-rate)',
    },
    kindHints: {
      RECURRING_BILLS: 'Projeta os templates de contas a pagar recorrentes.',
      OPEN_DOCUMENTS: 'Projeta contas não lançadas e/ou o saldo aberto de pedidos de compra.',
      ADHOC_RESIDUAL: 'Projeta a média do gasto residual dos últimos meses.',
      HEADCOUNT_PLAN: 'Projeta o custo do plano de contratações.',
      PAYROLL_RUNRATE:
        'Projeta o custo recorrente da folha a partir da última folha e dos colaboradores PJ mapeados. Não pode coexistir com o plano de headcount.',
    },
    kindHelp: {
      ADHOC_RESIDUAL: {
        whatTitle: 'O que é',
        what: 'O gasto que não vem de conta recorrente nem de pedido de compra — as compras avulsas do dia a dia.',
        howTitle: 'Como é calculado',
        howIntro: 'Em três passos.',
        step1Title: '1. Média do histórico.',
        step1:
          'Somamos esse gasto nos últimos meses completos e tiramos a média. O mês atual fica de fora, porque ainda não terminou e puxaria a média para baixo. Você escolhe quantos meses usar em "Meses de histórico".',
        step2Title: '2. Tiramos o que outra regra já explica.',
        step2:
          'Se a regra "Contas recorrentes" está ligada, o aluguel que vem de um template sai da média — aquela regra já projeta o aluguel. Se estiver desligada, o aluguel continua na média, porque senão ele não apareceria em lugar nenhum. É isso que impede a contagem dobrada.',
        step3Title: '3. Tiramos o que já foi lançado no mês.',
        step3:
          'A coluna fica ao lado do Realizado, então ela só mostra o que ainda falta acontecer.',
        exampleTitle: 'Exemplo.',
        exampleIntro: 'Média de R$ 1.000 por mês em despesas de escritório. Hoje é agosto.',
        tableMonth: 'Mês',
        tablePosted: 'Já lançado',
        tableProjected: 'Projetado',
        monthJune: 'junho',
        monthJuly: 'julho',
        monthAugust: 'agosto',
        monthSeptember: 'setembro',
        conclusion:
          'Junho já fechou acima da média, então não sobra nada a projetar. Nunca fica negativo: um mês em que você gastou a mais projeta R$ 0, nunca um crédito.',
      },
    },
    trailingMonths: 'Meses de histórico',
    includeUnpostedBills: 'Incluir contas não lançadas',
    includeOpenPurchaseOrders: 'Incluir pedidos de compra em aberto',
    filterInclude: 'Incluir apenas',
    filterExclude: 'Excluir',
    filterAll: 'Sem filtro — todas as fontes deste tipo',
    startTypingToSearch: 'Comece a digitar para pesquisar',
    selectorKeys: {
      vendorIds: 'Fornecedores',
      recurringBillTemplateIds: 'Templates de recorrência',
      purchaseOrderIds: 'Pedidos de compra',
      managerialAccountIds: 'Contas gerenciais',
      glAccountIds: 'Contas contábeis',
      costCenterIds: 'Centros de custo',
      departmentIds: 'Departamentos',
    },
    dimensions: 'Por dimensões',
    addId: 'Adicionar',
    idPlaceholder: 'UUID',
    save: 'Salvar conjunto',
    delete: 'Excluir',
    deleteConfirmTitle: 'Excluir este conjunto de regras?',
    deleteConfirm:
      'O nome fica livre para um novo conjunto. Relatórios já congelados continuam explicáveis. A exclusão é recusada se um template ainda aponta para este conjunto.',
    cancel: 'Cancelar',
    archivedBanner: 'Este conjunto está arquivado e não aparece no seletor de Projeções.',
    saved: 'Conjunto de regras salvo.',
    deleted: 'Conjunto de regras excluído.',
    loadError: 'Erro ao carregar os conjuntos de regras.',
    retry: 'Tentar novamente',
    unknownVariantTitle: 'Variante desconhecida',
    unknownVariant:
      'Este tipo de regra ainda não pode ser editado nesta tela. Os parâmetros desta regra são preservados ao salvar.',
    discardDraftTitle: 'Descartar alterações?',
    discardDraftBody:
      'Há alterações por gravar neste conjunto. Se continuar, essas alterações serão perdidas.',
    discardDraftConfirm: 'Descartar',
    discardDraftCancel: 'Continuar editando',
    draftIssue: {
      NAME_REQUIRED: 'Informe o nome do conjunto — ele vira o cabeçalho da coluna.',
      NAME_TOO_LONG: 'O nome deve ter no máximo 255 caracteres.',
      TRAILING_MONTHS: 'A janela residual deve ser um inteiro entre 1 e 60 meses.',
      OPEN_DOCUMENTS_EMPTY:
        'Uma regra de documentos em aberto precisa projetar contas não lançadas, pedidos em aberto, ou ambos.',
      SELECTOR_ID_INVALID: 'Cada filtro deve ser um UUID. Remova ou corrija o valor inválido.',
      PAYROLL_HEADCOUNT_COACTIVE:
        'Folha (run-rate) e plano de headcount não podem ficar no mesmo conjunto. Remova um dos dois.',
      PAYROLL_DUPLICATE: 'O conjunto pode ter no máximo uma regra de folha (run-rate).',
    },
    errors: {
      generic: 'Não foi possível salvar o conjunto de regras.',
      nameTaken:
        'Já existe um conjunto de regras com este nome. Escolha outro nome ou edite o conjunto existente.',
      nameInvalid: 'Informe um nome com até 255 caracteres para o conjunto de regras.',
      inUse:
        'Este conjunto ainda é usado por um template. Remova a coluna que aponta para ele antes de excluir.',
      paramsInvalid:
        'Os parâmetros não pertencem a este tipo de regra, ou um valor está fora do intervalo (janela residual de 1 a 60 meses; documentos em aberto precisam incluir contas, pedidos, ou ambos).',
      filterInvalid:
        'O filtro está incompleto ou inválido. Use include/exclude com ao menos um identificador UUID, ou omita o filtro para “todas as fontes”.',
      ruleIdInvalid: 'O identificador da regra é inválido. Recarregue o conjunto e tente de novo.',
      ruleIdDuplicate: 'A mesma regra aparece mais de uma vez na lista. Remova a duplicata.',
      ruleIdUnknown:
        'Uma regra nomeada não pertence a este conjunto. Remova o identificador ou recarregue.',
      ruleIdOnCreate:
        'Não envie o identificador de uma regra ao criar o conjunto. Salve primeiro e edite depois.',
      variantImmutable:
        'O tipo de uma regra existente não muda. Remova a regra e adicione outra do tipo novo.',
      foreignKey:
        'O registro referenciado não existe ou está fora do escopo. Verifique o filtro ou o conjunto.',
      uniqueConstraint:
        'Já existe um registro com estes dados. Abra o existente em vez de criar outro.',
      payrollHeadcountCoactive:
        'Folha (run-rate) e plano de headcount não podem ficar ativos no mesmo conjunto. Remova um dos dois.',
      payrollDuplicate: 'O conjunto pode ter no máximo uma regra de folha (run-rate).',
    },
  },
  snapshots: {
    freeze: 'Congelar',
    freezeTitle: 'Congelar este relatório',
    freezeDescription:
      'Cria um snapshot imutável do resultado avaliado — um artefato de auditoria. O relatório ao vivo continua sendo recalculado a cada leitura.',
    freezeConfirm: 'Congelar',
    freezeName: 'Nome do snapshot',
    freezeNamePlaceholder: 'ex.: fechamento ago/2026',
    freezePeriod: 'Período',
    freezeTemplate: 'Template',
    freezeScenario: 'Cenário',
    freezeSuccess: 'Snapshot congelado.',
    freezeDisabled: 'Selecione entidades e um período válido para congelar o relatório.',
    openList: 'Snapshots',
    listTitle: 'Snapshots',
    listDescription: 'Artefatos congelados deste template. Abra um as-of ou compare dois pontos.',
    listEmptyTitle: 'Nenhum snapshot',
    listEmptyDescription:
      'Congele o relatório que você está vendo para criar o primeiro artefato de auditoria.',
    listError: 'Erro ao carregar os snapshots.',
    retry: 'Tentar novamente',
    manual: 'Manual',
    daily: 'Diário',
    viewAsOf: 'Ver as-of',
    asOfDate: 'Data as-of',
    asOfDateAction: 'Abrir as-of',
    asOfBanner: 'Visualizando snapshot {{name}} (congelado em {{date}})',
    backToLive: 'Voltar ao ao vivo',
    diffVsLive: 'Ao vivo',
    diffFrom: 'De',
    diffTo: 'Para',
    diffAction: 'Abrir diff',
    diffBanner: 'Diff: {{from}} vs {{to}}',
    diffEmptyTitle: 'Nenhuma célula mudou',
    diffEmptyDescription: 'Os dois pontos têm os mesmos valores em todas as linhas.',
    diffError: 'Não foi possível calcular o diff.',
    columns: {
      line: 'Linha',
      period: 'Período',
      from: 'De',
      to: 'Para',
      delta: 'Delta',
      column: 'Coluna',
    },
    becameAvailable: 'Passou a existir',
    becameUnavailable: 'Deixou de existir',
    unitChanged: 'Unidade alterada',
    periodRange: '{{from}} – {{to}}',
    errors: {
      generic: 'Não foi possível concluir a operação de snapshot.',
      notFound: 'Nenhum snapshot encontrado para essa data ou identificador.',
      dateInvalid: 'A data do snapshot deve estar no formato AAAA-MM-DD.',
      scenarioNotDaily:
        'Esse cenário só pode ser congelado como snapshot manual. O lote diário captura apenas o Realizado.',
      scenarioRefInvalid:
        'A referência do cenário está incompleta. Orçado/Reforecast precisa de um orçamento; Projeções precisa de um conjunto de regras.',
      scopeMismatch:
        'Os dois snapshots congelam escopos diferentes ({{dimensions}}). Um delta trataria a mudança de escopo como movimento.',
      cellsInvalid: 'O artefato congelado está corrompido e não pode ser lido.',
      captureTimeout:
        'O congelamento excedeu o tempo de avaliação. Tente de novo com um período menor ou menos entidades.',
      templateArchived: 'Templates arquivados não podem ser congelados.',
      uniqueConstraint: 'Já existe um registro com esses dados. Abra o existente.',
      foreignKey: 'O registro referenciado não existe ou está fora do escopo.',
      dimensions: {
        entities: 'entidades',
        book: 'livro',
        scenario: 'cenário',
        entitiesOrBook: 'entidades ou livro',
      },
    },
    freezeEntities: 'Entidades',
    freezeBook: 'Livro',
    freezeDisabledAsOf: 'Saia da visualização as-of para congelar o relatório ao vivo.',
    freezeDisabledTemplateColumns:
      'O congelamento captura apenas a coluna Realizado — saia do modo Colunas do modelo para congelar.',
    freezeBookAmbiguous:
      'Não foi possível determinar o book do relatório — recarregue antes de congelar.',
    bookUnverified: 'não retornado',
    diffUnverifiedBanner: 'Snapshot: {{snapshot}} — Ao vivo: {{live}}',
    scopeEntities: 'entidades {{names}}',
    scopeScenario: 'cenário {{kind}}',
    scopeBook: 'livro {{book}}',
    diffPairReordered: 'De/Para foram invertidos para ordem cronológica.',
    diffUnlabeledRow: 'Linha',
    asOfScope: 'Escopo do snapshot',
    asOfFiltersLocked:
      'Os filtros do relatório ao vivo ficam pausados enquanto você vê o artefato.',
  },
  builder: {
    title: 'Construtor de DRE',
    createTitle: 'Novo relatório de DRE',
    openBuilder: 'Abrir construtor',
    save: 'Salvar template',
    saving: 'Salvando…',
    saved: 'Template salvo',
    discard: 'Descartar alterações',
    openReport: 'Abrir relatório',
    genericError: 'Não foi possível salvar o template.',
    tabs: {
      rows: 'Linhas',
      columns: 'Colunas',
      display: 'Exibição',
      modelagem: 'Modelagem',
    },
    list: {
      create: 'Novo relatório',
      archive: 'Arquivar',
      archiveTitle: 'Arquivar template',
      archiveConfirm:
        'Arquivar este template? Ele sai da lista ativa e não aceita mais alterações.',
    },
    wizard: {
      stepIdentity: 'Identidade',
      stepConfirm: 'Confirmar',
      next: 'Continuar',
      back: 'Voltar',
      create: 'Criar relatório',
      name: 'Nome',
      namePlaceholder: 'Ex.: DRE Gerencial 2026',
      description: 'Descrição',
      regime: 'Regime',
      fonte: 'Fonte de dados',
      dateBasis: 'Base de data',
      start: 'Começar com',
      startSeed: 'DRE Padrão (linhas oficiais)',
      startBlank: 'Template em branco',
      identityWarning:
        'Regime, fonte e base de data são imutáveis depois da criação. Escolha com cuidado.',
      caixaHidesDateBasis:
        'Templates de caixa não têm base de data — o período usa data de liquidação.',
      contabilGerencialCaution:
        'Com fonte Contábil, a base Gerencial usa a data de emissão do documento — o relatório deixa de bater com o balanço do razão em um mês específico.',
      confirmHint: 'Confirme a identidade. Esta escolha não poderá ser alterada.',
      confirmCheck: 'Entendi que regime, fonte e base de data não poderão ser alterados.',
      seedUnavailable:
        'Os rollups oficiais da DRE Padrão não estão disponíveis nesta fonte. O template foi criado em branco.',
    },
    canvas: {
      title: 'Canvas de linhas',
      addRow: 'Adicionar linha',
      addHeader: 'Adicionar cabeçalho',
      addSpacer: 'Adicionar espaçador',
      moveUp: 'Mover para cima',
      moveDown: 'Mover para baixo',
      remove: 'Remover linha',
      indent: 'Recuar',
      outdent: 'Recuar menos',
      pickRollup: 'Escolher rollup',
      newAggregation: 'Novo rollup de contas',
      newCalculated: 'Novo rollup calculado',
      editRollup: 'Editar rollup',
      noRollup: 'Sem rollup',
      formula: 'Fórmula',
      inverted: 'Inverter sinal (custos/despesas)',
      bold: 'Negrito',
      hide: 'Ocultar linha',
      empty: 'Nenhuma linha. Adicione um cabeçalho ou uma linha de rollup.',
      headerPlaceholder: 'Rótulo da seção',
      rowLabel: 'Rótulo da linha',
      rowLabelPlaceholder: 'Rótulo opcional (preservado no salvamento)',
    },
    columns: {
      title: 'Colunas padrão',
      add: 'Adicionar coluna',
      remove: 'Remover coluna',
      kind: 'Tipo',
      from: 'De',
      to: 'Até',
      label: 'Rótulo',
      scenario: 'Cenário (orçamento)',
      forecastRuleSet: 'Conjunto de regras de forecast',
      renewalTerms: 'Renovações assumidas',
      dynamicPtax: 'PTAX dinâmica',
      dynamicPtaxHelp:
        'Remensura a receita reconhecida ainda não recebida (Ativo de Contrato) pela PTAX atual. Receita já liquidada nunca é retraduzida.',
      rangeMode: {
        label: 'Período',
        fixed: 'Fixo',
        rolling: 'Rolante',
      },
      anchor: {
        label: 'Referência',
        lastClosedMonth: 'Último mês fechado',
        currentMonth: 'Mês atual',
      },
      fromOffset: 'De (meses)',
      toOffset: 'Até (meses)',
      resolvedPreview: '{{from}} → {{to}} hoje',
      rollingPreset: {
        action: 'Rolling forecast',
        title: 'Aplicar Rolling forecast',
        description:
          'Substitui as colunas atuais por Realizado (-6 a 0) e Projeção (+1 a +12), ancoradas no último mês fechado.',
        selectRuleSet: 'Selecione um conjunto de regras',
        noRuleSets: 'Nenhum conjunto de regras ativo está disponível.',
        cancel: 'Cancelar',
        apply: 'Aplicar preset',
      },
      empty: 'Nenhuma coluna. O preview usa o recorte padrão do relatório.',
      issues: {
        budget: 'Orçado exige um orçamento ORIGINAL.',
        budgetRevision: 'Reforecast exige uma revisão de orçamento.',
        forecastRuleSet: 'Este cenário exige um conjunto de regras de forecast.',
        caixaScenario: 'Templates de caixa aceitam apenas o cenário Realizado.',
        tooMany: 'No máximo 8 colunas.',
        periods: 'Cada coluna precisa de 1 a 120 períodos mensais.',
        relativeRange: 'Use meses inteiros entre -60 e +60, com “De” menor ou igual a “Até”.',
        tooManyEntities: 'A avaliação aceita no máximo 50 entidades.',
      },
    },
    display: {
      title: 'Exibição',
      showZeroRows: 'Mostrar linhas zeradas',
      indentNested: 'Recuar linhas aninhadas',
      percentDecimals: 'Casas decimais de percentual',
    },
    preview: {
      title: 'Preview ao vivo',
      loading: 'Avaliando template…',
      empty: 'Salve o template e adicione linhas para ver o preview.',
      hoverHint: 'Passe o mouse numa linha para destacar o canvas e o preview juntos.',
      missingMetric: 'Métrica sem ponto neste período — a célula ficou vazia.',
      missingOperand: 'Operando ausente entra como 0.',
      divisionByZero: 'Divisão por zero entra como 0.',
      columnIssues: 'Corrija as colunas antes de avaliar o preview.',
      // Fallback for a preview evaluation that failed with no recognised stable code.
      // The preview NEVER saves, so the builder's save-failure copy must not be reused.
      evaluationFailed: 'Não foi possível calcular o preview. Tente de novo.',
      scale: {
        fullWithCurrency: 'Valores em {{symbol}}',
        thousandsWithCurrency: 'Valores em {{symbol}} mil',
        millionsWithCurrency: 'Valores em {{symbol}} milhões',
        full: 'Escala: cheia',
        thousands: 'Escala: milhares',
        millions: 'Escala: milhões',
      },
    },
    consent: {
      title: 'Consentimento de restatement',
      description:
        'Esta alteração alimenta snapshots já reportados. Confirme o opt-in e informe o motivo (mínimo 10 caracteres).',
      optIn: 'Autorizo a alteração em períodos já reportados',
      reason: 'Motivo da alteração',
      reasonPlaceholder: 'Descreva por que o template precisa mudar…',
      confirm: 'Salvar com consentimento',
      cancel: 'Cancelar',
    },
    rollupEditor: {
      title: 'Editor de rollup',
      name: 'Nome',
      description: 'Descrição',
      kind: 'Tipo do rollup',
      kindAggregation: 'Agregação de contas',
      kindCalculated: 'Fórmula calculada',
      kindLocked: 'O tipo de um rollup existente não pode ser alterado.',
      switchWarn: 'Trocar entre agregação e fórmula apaga a seleção ou a fórmula. Continuar?',
      accountTypes: 'Tipos de conta',
      accountIds: 'Contas específicas',
      format: 'Formato',
      parent: 'Rollup pai',
      noParent: 'Nenhum',
      save: 'Salvar rollup',
      create: 'Criar rollup',
      cancel: 'Cancelar',
      formulaHint:
        'Subtotais são fórmulas, nunca a soma das linhas filhas. Operando ausente = 0; divisão por zero = 0.',
      formulaIncomplete: 'Fórmula incompleta — complete os operandos antes de salvar.',
      extraAccountTypes: 'Tipos retidos (somente leitura — o editor não oferece estes checkboxes).',
      addAssumption: 'Premissa',
      addMetric: 'Métrica',
      createAssumption: 'Nova Premissa',
      createMetric: 'Nova métrica',
    },
    filters: {
      title: 'Filtros',
      add: 'Adicionar filtro',
      dimension: 'Dimensão',
      operator: 'Operador',
      value: 'Valor',
      joinAnd: 'E',
      joinOr: 'OU',
      readOnly:
        'Filtro somente leitura — o editor não consegue autorar este predicado. Ele será preservado no salvamento.',
      empty: 'Nenhum filtro. Sem filtro, o rollup usa todas as contas do tipo/seleção.',
      notNullHint: 'Operadores NOT_* também passam linhas com valor nulo.',
      amountHint: 'Filtros de valor forçam avaliação linha a linha — pode ser mais lento.',
    },
    errors: {
      generic: 'Não foi possível salvar. Verifique os dados e tente de novo.',
      nameTaken: 'Já existe um template com este nome neste tenant.',
      rollupInUse: 'Este rollup está em uso e não pode ser arquivado.',
      rollupRefNotFound: 'Um rollup referenciado não existe ou está arquivado.',
      rollupParentCycle: 'O rollup pai formaria um ciclo.',
      accountRefShape: 'A referência de conta tem formato inválido.',
      accountSourceMismatch: 'A fonte da conta não coincide com a fonte do rollup.',
      accountNotFound: 'A conta referenciada não existe neste tenant.',
      accountDuplicate: 'A mesma conta aparece mais de uma vez no rollup.',
      segmentFilterContabil: 'Filtro de segmento gerencial não é permitido em rollup CONTABIL.',
      segmentTextFilterUnsupported:
        'Filtros de rollup aceitam apenas valores predefinidos; valores de texto livre ainda não podem ser filtrados.',
      segmentFilterValueConflict:
        'Use valores predefinidos ou texto livre no filtro de segmento, não os dois juntos.',
      segmentTextValueNotAllowed:
        'Este campo não aceita texto livre. Escolha um valor predefinido do catálogo.',
      segmentPredefinedValueNotAllowed:
        'Este campo não possui catálogo de valores. Remova os valores predefinidos deste filtro.',
      segmentTextValueTooLong: 'O texto livre do filtro deve ter no máximo 200 caracteres.',
      formulaRequired: 'Rollup CALCULATED precisa de uma fórmula.',
      aggregationNoFormula: 'Rollup AGGREGATION não pode ter fórmula.',
      aggregationFormatNotMonetary:
        'Um rollup AGGREGATION soma valores monetários; os formatos PERCENT e NUMBER só valem para rollups CALCULATED. Use CURRENCY ou converta para um rollup calculado.',
      calculatedNoSelection: 'Rollup CALCULATED não pode ter seleção de contas ou tipos.',
      formulaInvalid: 'A fórmula é inválida.',
      formulaTooDeep: 'A fórmula ultrapassa a profundidade máxima de 8.',
      formulaCycle: 'A fórmula forma um ciclo entre rollups.',
      formulaDimensionMismatch: 'Os operandos da fórmula não compartilham a mesma dimensão.',
      templateImmutableField:
        'Regime, fonte e base de data não podem ser alterados depois da criação.',
      templateArchived: 'Templates arquivados não aceitam alteração.',
      dateBasisCaixa: 'Templates de caixa não podem ter base de data.',
      rowRollupRequired: 'Linha de valor precisa de um rollup.',
      rowLabelRequired: 'Cabeçalho de seção precisa de um rótulo.',
      rowRollupForbidden: 'Cabeçalho de seção não pode apontar para um rollup.',
      rowSourceMismatch: 'O rollup da linha não usa a mesma fonte do template.',
      defaultColumnsInvalid: 'As colunas padrão são inválidas.',
      relativeRangeInvalid:
        'Revise a faixa rolante: use meses inteiros entre -60 e +60 e mantenha o início antes do fim.',
      displaySettingsInvalid: 'As opções de exibição são inválidas.',
      definitionPeriodReported:
        'A alteração alimenta um snapshot já reportado. Confirme o consentimento e informe o motivo.',
      changeReasonRequired:
        'Informe um motivo com pelo menos 10 caracteres para autorizar o restatement.',
      tenantBusy: 'Outra alteração de definição está em andamento neste tenant. Tente de novo.',
      scenarioBookScopeUnsupported:
        'O livro selecionado não cobre este escopo. Ajuste as entidades ou o livro.',
    },
  },
  modelagem: {
    title: 'Modelagem',
    description: 'Premissas, métricas e ajustes de linha no mesmo fluxo do construtor de DRE.',
    assumptions: 'Premissas',
    metrics: 'Métricas',
    overrides: 'Ajustes de linha',
    restatementQueue: 'Solicitações desta sessão',
    sessionQueueNote:
      'Pedidos pendentes são aprovados por outro usuário e só aparecem aqui até recarregar a página. Quem aprova revisa no ponto da métrica — o ponto da API não traz o restatement pendente.',
    emptyAssumptions: 'Nenhuma Premissa neste tenant.',
    emptyMetrics: 'Nenhuma série de métrica.',
    emptyOverrides: 'Nenhum ajuste de linha neste template.',
    emptyQueue: 'Nenhuma solicitação nesta sessão. Elas desaparecem ao recarregar.',
    create: 'Criar',
    edit: 'Editar',
    delete: 'Excluir',
    save: 'Salvar',
    cancel: 'Cancelar',
    key: 'Chave',
    name: 'Nome',
    unit: 'Unidade',
    provenance: 'Proveniência',
    budget: 'Orçamento',
    forecastRuleSet: 'Conjunto de regras',
    values: 'Valores por mês',
    fieldHelp: {
      key: 'Identificador usado nas fórmulas dos rollups calculados: assumption(chave). Não pode ser alterado depois de criada.',
      name: 'Nome de exibição da Premissa nas listas e no drill-down. Não afeta o cálculo.',
      unit: 'Unidade mostrada ao lado do valor no drill-down (ex.: rate, %, BRL). Convenção: "rate" usa fração — 0.05 significa 5%. Não afeta o cálculo.',
      budget:
        'Vincula a Premissa a um orçamento: só as colunas Orçado/Reforecast desse orçamento a enxergam. Preencha este campo OU o Conjunto de regras — exatamente um. Colunas Realizado nunca usam Premissas.',
      forecastRuleSet:
        'Vincula a Premissa a um conjunto de regras de projeção: só as colunas de projeção desse conjunto a enxergam. Preencha este campo OU o Orçamento — exatamente um.',
      provenance:
        'Nota opcional de auditoria sobre a origem do número. Apenas informativa; não afeta o cálculo.',
      values:
        'Um valor por linha, no formato AAAA-MM=valor (ex.: 2026-08=0.05). Mês ausente não tem valor — a célula que depender dele fica vazia.',
    },
    period: 'Período',
    value: 'Valor',
    addValue: 'Adicionar mês',
    entity: 'Entidade',
    currency: 'Moeda',
    points: 'Pontos',
    sources: 'Fontes',
    deletePoint: 'Excluir ponto',
    requestRestatement: 'Pedir restatement',
    pending: 'Pendente',
    approved: 'Aprovado',
    overrideAmount: 'Valor do ajuste',
    effectiveFrom: 'Vigente de',
    effectiveTo: 'Vigente até',
    book: 'Livro',
    removeOverride: 'Remover ajuste',
    createOverride: 'Criar ajuste',
    overrideFromCell: 'Ajustar esta célula',
    noTemplate: 'Abra um template para criar ajustes de linha.',
    rollup: 'Linha (rollup)',
    selectBook: 'Selecione o livro',
    selectRollup: 'Selecione o rollup',
    bookRequired: 'Escolha o livro antes de salvar o ajuste.',
    saveDisabledHint: 'Preencha os campos obrigatórios para salvar.',
    boundBudget: 'Orçamento',
    boundForecast: 'Conjunto de regras',
    boundUnbound: 'Sem vínculo de cenário',
    boundEntity: 'Entidade',
    sourcesDate: 'Data',
    sourcesSource: 'Fonte',
    sourcesAmount: 'Valor',
    sourcesForbidden: 'Sem permissão para ver as fontes do ponto (ar:contract:read).',
    sourcesEmpty: 'Este ponto não tem fontes de billing.',
    sourcesEmptyTenant: 'Este ponto é a própria fonte — não há fontes de billing.',
    deleteConfirmTitle: 'Excluir',
    deleteAssumptionConfirm: 'Excluir esta Premissa? A exclusão não pode ser desfeita.',
    deleteSeriesConfirm: 'Excluir esta série de métrica? A exclusão não pode ser desfeita.',
    deletePointConfirm: 'Excluir este ponto da série?',
    deleteOverrideConfirm: 'Remover este ajuste de linha?',
  },
  metrics: {
    errors: {
      generic: 'Não foi possível salvar a métrica.',
      keyInvalid:
        'A chave da métrica usa só letras minúsculas, números, ponto e underscore. Exemplo: receita_por_cliente.',
      keyReserved: 'Essa chave é reservada para métricas de sistema (billing.*).',
      valueInvalid: 'O valor da métrica precisa ser um número decimal.',
      periodInvalid: 'O período da métrica precisa ser YYYY-MM.',
      periodFuture: 'Não é possível materializar um período futuro.',
      pointDuplicate: 'Já existe um ponto para esse mês nesta série.',
      seriesReadOnly: 'Séries de sistema (billing) são somente leitura.',
      currencyImmutable: 'A moeda da série não pode ser alterada depois da criação.',
      currencyMixed: 'Os pontos da série não podem misturar moedas.',
      currencyScopeMismatch: 'A moeda da métrica não coincide com a moeda do escopo avaliado.',
      unitImmutable: 'A unidade da série não pode ser alterada depois da criação.',
      unitMixed: 'Os pontos da série não podem misturar unidades.',
      closedPeriod:
        'Esse mês já está fechado ou reportado. Peça um restatement com justificativa e aprovação.',
      restatementReasonRequired: 'Informe um motivo para o restatement da métrica.',
      restatementNoChange: 'O restatement precisa alterar o valor do ponto.',
      restatementNotRequired: 'Esse mês não exige restatement — edite o ponto diretamente.',
      restatementNotPending: 'Esse restatement não está pendente.',
      selfApproval: 'Quem pediu o restatement não pode aprová-lo.',
      restatementStale: 'O ponto mudou desde o pedido. Peça o restatement de novo.',
      evidenceMissing: 'Falta a evidência de materialização desta métrica de billing.',
      evidenceMismatch: 'A evidência de materialização não confere com o ponto.',
      entityNotFound: 'A entidade da métrica não existe neste tenant.',
    },
  },
};

export const fpaEn = {
  payroll: payrollEn,
  list: {
    title: 'FP&A — DRE',
    description: 'DRE templates evaluated over the GL ledger or managerial classifications.',
    showArchived: 'Show archived',
    columns: {
      name: 'Template',
      updatedAt: 'Updated at',
    },
    filters: {
      columnTooltip: 'Filter column',
      noBase: 'No basis',
      all: 'All',
    },
    archivedBadge: 'Archived',
    empty: {
      title: 'No DRE templates',
      description:
        'Create a template in the builder or open an existing one. Once a template is created it shows up here.',
    },
    emptyArchivedHint: 'No active templates. Enable "Show archived" to see archived ones.',
    noMatch: {
      title: 'No templates match the filters',
      description: 'Adjust the column filters to see other templates.',
      clear: 'Clear filters',
    },
    loading: 'Loading templates…',
    error: 'Failed to load templates.',
    retry: 'Retry',
  },
  meta: {
    regime: 'Regime',
    fonte: 'Fonte',
    dateBasis: 'Base de Data',
    regimeValues: {
      COMPETENCIA: 'Competência',
      CAIXA: 'Caixa',
    },
    fonteValues: {
      CONTABIL: 'Contábil',
      GERENCIAL: 'Gerencial',
    },
    dateBasisValues: {
      COMPETENCIA: 'Competência',
      GERENCIAL: 'Gerencial',
    },
    scenarioValues: {
      REALIZADO: 'Realizado',
      ORCADO: 'Orçado',
      REFORECAST: 'Reforecast',
      RECEITA_PROJETADA: 'Projected Revenue',
      DESPESA_PROJETADA: 'Projected Expense',
      PROJECAO: 'Projections',
    },
  },
  report: {
    reportingCurrency: 'Reporting currency',
    functionalCurrency: 'Functional currency',
    fxDetails: 'Applied FX · {{currency}}',
    fxPolicy:
      'Combined report without intercompany eliminations. Actuals use event-date PTAX; monthly metrics use month-end quotes. Missing quotes prevent conversion. Posted journals remain unchanged.',
    fxAsOf: 'Quotes available as of {{date}}.',
    fxQuoteDate: 'PTAX {{date}} · event {{requested}}',
    fxNoConversion: 'Amounts already use the reporting currency.',
    fxSnapshotUnavailable:
      'Select one entity in its functional currency to freeze. Consolidated freezing is not yet available.',

    backToList: 'Templates',
    header: {
      minimize: 'Minimize header',
      expand: 'Expand header',
    },
    periodFrom: 'From',
    periodTo: 'To',
    combinedBadge: 'Combinado (sem eliminações)',
    lineColumn: 'Line',
    refreshing: 'Refreshing evaluation…',
    error: 'Failed to evaluate the template.',
    errors: {
      reportingCurrencyRequired:
        'Choose a reporting currency to combine entities with different functional currencies.',
      reportingFxUnavailable:
        'PTAX is unavailable for this report. No rate was estimated. Try again later.',
      reportingScopeChanged: 'Entity books or currencies changed. Refresh the report.',
      reportingScenarioUnsupported:
        'Combined reporting currently supports Actuals. Select one entity in its functional currency to compare scenarios.',
      multiCurrencyScope:
        'The selected entities use different functional currencies — amounts are never summed across currencies. Select entities sharing one currency.',
      bookAmbiguous:
        'Could not resolve the entity posting book. Check its accounting configuration.',
      columnsRequired:
        'This template has no default columns yet. Add a column on the Colunas tab to evaluate the report.',
      bookNotAssigned:
        'The requested posting book does not belong to every selected entity. Omit the explicit book to resolve each entity automatically.',
      bookNotFound: 'The requested ledger book does not exist or was removed.',
      entityScopeRequired: 'Select at least one entity to evaluate the template.',
      scenarioEntityScope:
        "The selected budget belongs to an entity outside this evaluation's scope. Include the budget's entity in the selection.",
      scenarioBookMismatch:
        'The selected budget is bound to a different ledger book. Compare budget and actuals on the same book.',
      scenarioCurrencyMismatch:
        "The budget's currency differs from the evaluated scope's currency — amounts are never summed across currencies.",
      scenarioUnattributable:
        'The budget has lines without an account compatible with this template\'s Fonte (e.g. cost-center envelopes or the "Outros" fallback line); the column would omit part of the budget.',
      scenarioDimensionOverlap:
        'The budget plans the same account under different dimension granularities; summing them would double-count the plan.',
      scenarioKindMismatch:
        'The scenario kind does not match the budget version (Orçado requires the original version; Reforecast requires a revision).',
      scenarioPeriodGrain: "The budget's calendar is not monthly; DRE columns are month-grained.",
      scenarioMultiCurrency:
        'The budget version mixes currencies across its lines — amounts are never summed across currencies. Restate the budget in a single currency.',
      scenarioFxRateUnresolved:
        'No PTAX quote could be obtained for {{from}} to {{to}}, so the projected column was refused rather than showing an estimated figure or dropping the {{from}} lines. Try again later.',
      scenarioProjectionCurrencyDivergence:
        'The revenue and expense halves of the projection resolved different functional currencies for the same scope. Check the functional currency configured on the selected entities ledger books.',
      scenarioProjectionRateDivergence:
        'The revenue and expense halves of the projection converted {{from}} using different PTAX bulletins in the same column. The column was refused rather than publishing totals converted at two rates — re-run the report.',
      scenarioInvalidParams:
        'Invalid parameters for the projected-revenue scenario (assumed renewals between 0 and 10).',
      scenarioUnsupported: 'This template does not support the selected scenario.',
      scenarioCellCollision:
        'Projected revenue and expense both attributed a value to the same line — the column was refused rather than silently picking one side.',
      scenarioForecastRuleSetRequired:
        'Projections needs a forecast rule set. Author one via agent, or pick an existing set.',
      bookScopeEntityNotGoverned:
        'The selected book does not govern every entity in scope. Adjust the entity selection or the book.',
      bookScopeAccountOutsideCoa:
        "The projection references accounts outside the selected book's chart of accounts.",
      bookScopeUnsupported:
        'The book scope does not support this projection. Adjust the entity selection or the book.',
      assumptionUnresolved:
        'The formula points at a Premissa that does not exist in this scenario. Create the Premissa or fix the formula.',
      encargosJurisdiction:
        'Brazilian statutory charges do not apply to this entity. Remove it from the selection or pick another entity.',
      encargosEntityPolicyNotEffective:
        "This entity has its own encargos policy, but no window covers the requested period. Configure a rate for the period or adjust the report's range.",
      scenarioCurrencyUnresolved:
        "Could not determine this scenario's currency. Check that the selected entities have a ledger book.",
      periodsLimit:
        'The range has more months than the report can show. Shorten the period (maximum {{limit}} months).',
      drilldownScenarioUnsupported:
        'This scenario cell has no detailed composition. Open an aggregation cell on Realizado or the budget.',
      drilldownFxPinMissing:
        'This cell is decomposed at exactly the exchange rates it was computed with. The scope has changed since, so it can no longer be broken down at those rates — re-run the report and open the cell again.',
      drilldownFxPinPairMissing:
        'This cell was computed without any {{from}} amounts, but its composition now finds {{from}} lines. Converting them at today’s rate would add an amount the cell does not contain — re-run the report to pick them up.',
      drilldownFxPinTargetChanged:
        'The scope’s functional currency has changed since this cell was computed, so the rates pinned on it point at a different currency. Re-run the report and open the cell again.',
      drilldownHandleInvalid:
        'This report is out of date with the server — re-run the report and try the drill-down again.',
      forecastFilterInvalid:
        'The forecast-rule filter points at an invalid record. Fix the filter on the rule set.',
      payrollRunRateSourceMissing:
        'The payroll rule is active but its source was not loaded. Reload the report or remove the rule.',
      payrollRunRateDuplicate: 'A rule set may contain only one payroll (PAYROLL_RUNRATE) rule.',
      payrollRunRateHeadcountCoactive:
        'Modelled payroll and the hiring plan cannot be active in the same rule set — that would double-count personnel.',
      closedPeriod:
        'That month is already closed or frozen in a snapshot. To change it, request a restatement with a reason.',
      overridePopulationUndetermined:
        'Could not determine the accounts for this line, so the adjustment was not applied. Use a line with explicit accounts.',
      overrideRollupKindInvalid: 'A subtotal cannot be pinned. Adjust a detail line instead.',
      overrideCurrencyMismatch:
        "The adjustment is in a different currency than the report. Recreate it in the book's currency.",
      snapshotDrilldownUnavailable:
        'This snapshot has no frozen composition for this cell — it was captured before this feature existed, or could not be reconciled with the frozen value at capture time.',
    },
    scenarioColumn: {
      label: 'Scenarios',
      placeholder: 'Select a budget',
      templateDefaults: 'Template columns',
      none: 'Realizado only',
      ruleSet: 'Rule set',
      ruleSetPlaceholder: 'Select a rule set',
      projectionsNeedRuleSet: 'Projections requires an active forecast rule set.',
      projectionsManageRuleSets: 'Create or activate a rule set',
    },
    displayOptions: {
      trigger: 'View options',
      amountScale: {
        label: 'Amount granularity',
        FULL: 'Full numbers',
        THOUSANDS: 'Thousands (k)',
        MILLIONS: 'Millions (M)',
      },
      percentDecimals: {
        label: 'Percentage precision',
        decimals0: 'No decimal places',
        decimals1: '1 decimal place',
        decimals2: '2 decimal places',
      },
      signColors: 'Color positives/negatives',
      showCurrency: 'Show currency',
      showCents: 'Show cents',
      showNaoClassificado: 'Show Não Classificado',
      showNaoClassificadoHint:
        'Reconciliation row holding the amount that could not be attributed to a DRE line. It is never summed into a total — hiding it changes no other number.',
      saveError: 'Failed to save the view options.',
    },
    coverage: {
      title: 'Projection coverage',
      explainer:
        'Caveats on this column: items omitted or partially projected, and currency conversions applied.',
      truncatedCount: '{{shown}} of {{total}}',
      truncatedTitle: 'Projection coverage — {{shown}} of {{total}} caveats',
      truncatedNote:
        'The list was capped at {{shown}} of this column’s {{total}} caveats. Disclosures — currency conversions, manual pins and assumptions — come first, so none of those were left out.',
      summarizedCount: '{{total}} ({{reasons}} reasons)',
      summarizedTitle: 'Projection coverage — {{total}} caveats across {{reasons}} reasons',
      summarizedNote:
        'This column’s bulk caveats were grouped by reason instead of listed one by one. Disclosures — currency conversions, manual pins and assumptions — are still listed individually above.',
      summaryGroupTitle: '{{count}} — {{reason}}',
      summarySampleNote: 'showing {{shown}} of {{total}}',
      partial: 'partial',
      fx: {
        badge: 'converted',
        rate: '1 {{from}} = {{rate}} {{to}}',
        quoteDate: 'PTAX bulletin of {{date}} ({{provider}}), applied to every projected month',
        convertedCount_one: '{{count}} converted line',
        convertedCount_other: '{{count}} converted lines',
      },
      relativeRange: {
        resolvedAnchor: 'Anchor resolved to {{month}}',
      },
      cancelledWindows_one: '{{count}} cancelled service window suppressed',
      cancelledWindows_other: '{{count}} cancelled service windows suppressed',
      reasons: {
        NO_CLOSED_PERIOD:
          'No accounting period has been closed; the range used the previous calendar month',
        CONVERTED_AT_PTAX:
          "Amounts converted into the scope's functional currency at the PTAX selling rate",
        NO_START_DATE: 'No start date',
        NO_TERM_END_DATE: 'Contract without a final term',
        NO_EFFECTIVE_VERSION: 'No effective version',
        NO_PROJECTABLE_ITEMS: 'No projectable items',
        UNATTRIBUTABLE: 'No account to attribute to a DRE line',
        FILTER_REFERENCE_UNRESOLVED: 'The filter points at a record that no longer exists',
        PAYROLL_HISTORICAL_RUN_RATE: 'Payroll carried at its historical run-rate, not modelled',
        UNPOSTED_BEFORE_RANGE: 'Unposted obligations in competency months before this range',
        NO_COMPETENCY_DATE: 'No competency date',
        NO_AMOUNT: 'No amount set',
        OCCURRENCE_CAP_REACHED: 'Projected-occurrence cap reached',
        ENDED_SOURCE_IN_RESIDUAL:
          'Ended source — its history is inside the residual average, not its own line',
        NO_PROJECTABLE_RULES: 'The rule set has no active rules',
        PAYROLL_EXCLUDED_BY_HEADCOUNT_PLAN:
          'Payroll excluded from the residual — the hiring plan owns personnel',
        HEADCOUNT_PLAN_UNATTRIBUTED:
          'Positions have no managerial account — the amount went to Não Classificado; set the account on the Posição',
        ENCARGOS_JURISDICTION_UNSUPPORTED:
          'BR statutory charges do not apply to this entity — personnel omitted from the projection',
        ENCARGOS_POLICY_NOT_EFFECTIVE:
          'This entity has its own encargos policy, but no window covers the period — personnel omitted from the projection',
        PAYROLL_ROSTER: 'Last monthly payroll roster, excluding terminations',
        JOINERS_NOT_MODELLED: 'Future joiners are not derived from payroll',
        PAYROLL_PROVISION_BRANCH:
          'Provisions follow the tenant branch (imported or calculated on the run)',
        UNRECOGNISED_RUBRIC: 'Unrecognised rubrics fall through to the earning base',
        PAYROLL_MANAGERIAL_UNRESOLVED:
          'Payroll managerial account was not resolved — the row stayed on Fonte CONTABIL',
        PAYROLL_FILTER_EXCLUDED:
          'Employee outside the payroll driver scope (cost center, project, department or entity).',
        UNMAPPED_COLLABORATOR: 'PJ collaborator has no party or vendor link',
        PAYROLL_EXCLUDED_BY_PAYROLL_RUNRATE:
          'Payroll and mapped PJ left the residual — the variant models personnel',
        PAYROLL_BASE_RUN_STALE:
          "This entity's last monthly payroll is older than the window — projection was refused",
        PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS:
          'A termination has no CPF — the employee was not projected to avoid erasing a rehire',
        PAYROLL_CURRENCY_MISMATCH:
          "Payroll or the PJ collaborator's bill is in a currency other than the book's functional currency — not converted",
        PAYROLL_PROVISION_MISSING:
          'Not publishable: imported provision missing for members of this entity — emission was refused',
        INSUFFICIENT_HISTORY:
          'Insufficient RPA history for the configured window and minimum occurrences.',
        UNAVAILABLE: 'CLT payroll unavailable. Import monthly payroll to project this component.',
        NO_SOURCE_DATA: 'No imported payroll of the enabled type exists in the configured window.',
        PAYROLL_EMPLOYEE_SKIPPED: 'Employees with a zero or negative amount were omitted',
        PAYROLL_COLLABORATOR_STALE:
          'PJ collaborator has no bill in the trailing window — the rate is not projected forever',
        PAYROLL_COLLABORATOR_NO_EMISSION:
          'PJ collaborator produced no projectable occurrence — the template is already materialized or outside the window',
        PAYROLL_REFUSED_COVERED_BY_RESIDUAL:
          "This entity's payroll emission was refused — the ad-hoc residual covers the posted history",
        PAYROLL_TEMPLATE_NOT_MODELLED:
          'Payroll template with no coverage — no active variant is projecting this PJ contract',
        PAYROLL_TEMPLATE_WINDOW_DRIFTED:
          "The payroll template's vigência changed after the document was created — classification kept, not recalculated",
        UNRESOLVED: 'Premissa not found in this scenario',
        REALIZADO_HAS_NO_PREMISSA: 'Realizado does not use Premissas — the cell was left blank',
        SCENARIO_KIND_INCONSISTENT: 'This Premissa belongs to a different scenario kind',
        CURRENCY_MISMATCH: 'Adjustment in another currency — not summed',
        AGGREGATION_PIN_SKIPPED: 'A subtotal cannot take a pinned amount — adjust the detail line',
        CALCULATED_PIN_SKIPPED: 'Calculated line: the pinned amount does not enter subtotals',
        NULL_CELL_SKIPPED:
          'There was no amount this month — the adjustment does not invent a number',
        PIN_NOT_CONTAINED: 'This adjustment does not enter any DRE subtotal',
        PIN_ACCOUNTS_UNMATERIALIZED:
          "This adjustment's accounts are not yet on the period's chart of accounts",
        OPERAND_NOT_ADJUSTED: 'One formula part was not adjusted, so the pin is not counted twice',
        OPERANDS_ONLY: 'The adjustment landed only on formula parts, not the total',
        PRIOR_PERIOD_OUTSIDE_COLUMN: 'The formula needs a month this column does not show',
      },
      flags: {
        NO_CLOSED_PERIOD_FALLBACK: 'Temporary anchor until the first period is closed',
        USAGE_EXCLUDED: 'Usage revenue is not projected',
        PAYROLL_RUN_RATE: 'Payroll inside the residual historical average',
        UNSTAMPED_PAYROLL_JOURNAL:
          'Payroll journal with no cost centre/department — covered at the entity',
        UNATTRIBUTED: 'No account to attribute to a DRE line',
        INCLUDES_BOOK_REMEASUREMENT:
          'Includes FX remeasurement over the booked amount (dynamic PTAX)',
      },
    },
    templateError: 'Failed to load the template.',
    retry: 'Retry',
    notFound: 'Template not found.',
    invalidRange: 'The start period must be on or before the end period.',
    tooManyPeriods: 'Maximum range of {{max}} months.',
    noEntities: 'Select at least one entity to evaluate the template.',
    emptyValuesNotice:
      'This template only has structural rows (headers and spacers) — add rollup rows to see values.',
    noRows: 'This template has no rows.',
    drilldown: {
      disabledAsOf:
        'Drill-down shows the live composition — unavailable on a snapshot until the frozen trail exists.',
    },
    buckets: {
      naoClassificado: 'Não Classificado',
      semEventoDeCaixa: 'Sem Evento de Caixa',
      naoClassificadoHint: "Amounts this template's Fonte cannot attribute to any rollup.",
      semEventoDeCaixaHint:
        'Accrual amounts with no traceable cash settlement in this Regime Caixa template.',
    },
  },
  entitySelect: {
    label: 'Entities',
    placeholder: 'Select entities',
    allSelected: 'All entities',
    selected_one: '{{count}} entity',
    selected_other: '{{count}} entities',
    selectAll: 'Select all',
    clear: 'Clear selection',
    loading: 'Loading entities…',
    error: 'Failed to load entities.',
    empty: 'No entities available.',
  },
  drilldown: {
    title: 'Cell composition',
    description: 'Source rows that make up this cell value.',
    period: 'Period',
    scenario: 'Cenário',
    entities: 'Entities',
    entitiesCount_one: '{{count}} entity',
    entitiesCount_other: '{{count}} entities (combinado, sem eliminações)',
    cellValue: 'Cell value',
    reconcilingTotal: 'Reconciling total',
    roundingDifference: 'Rounding difference',
    loading: 'Loading composition…',
    error: 'Failed to load the composition.',
    emptyRows: 'No source rows for this value.',
    // ALC-2608: banner shown instead of `description` when the drawer serves a
    // snapshot's FROZEN composition rather than a live fetch.
    frozenComposition: 'Composition frozen on {{date}}.',
    frozenTruncated: 'Showing {{shown}} of {{total}} rows.',
    columns: {
      type: 'Source',
      description: 'Description',
      dateUsed: 'Date used',
      amount: 'Amount',
    },
    rowTypes: {
      journal_line: 'Journal line',
      classification_split: 'Managerial classification',
      settlement_allocation: 'Cash settlement',
      budget_line: 'Budget line',
      metric_point: 'Metric point',
      formula_operand: 'Formula operand',
      projected_revenue: 'Projected revenue',
      projected_expense: 'Projected expense',
      projected_revenue_fx_remeasurement: 'PTAX remeasurement',
    },
    provenance: {
      RECOGNIZED: 'Recognized',
      SCHEDULED: 'Scheduled',
      PROJECTED_TERM: 'Projected (in-term)',
      PROJECTED_RENEWAL: 'Projected (renewal)',
      // Projected expense (ALC-2116): commitment vs assumption. Only
      // EXISTING_DOCUMENT is an obligation already on file.
      EXISTING_DOCUMENT: 'Existing document',
      RECURRING_OCCURRENCE: 'Recurring occurrence',
      HISTORICAL_AVERAGE: 'Historical average',
      HEADCOUNT_PLAN: 'Hiring plan',
      PAYROLL_RUNRATE: 'Payroll (run-rate)',
    },
    /** Fallback when a projected row carries neither a label nor a document. */
    sourceKinds: {
      BILL: 'Bill',
      PURCHASE_ORDER: 'Purchase order',
      RECURRING_BILL_TEMPLATE: 'Recurring template',
      ADHOC_RESIDUAL: 'Ad-hoc residual',
      HEADCOUNT_POSITION: 'Position',
      PAYROLL_EMPLOYEE: 'CLT employee',
      PAYROLL_COLLABORATOR: 'PJ collaborator',
    },
    dateSources: {
      JOURNAL: 'Journal date',
      BILL_MANAGERIAL: 'Bill managerial date',
      BILL_ISSUE: 'Bill issue date',
      INVOICE_MANAGERIAL: 'Invoice managerial date',
      INVOICE_ISSUE: 'Invoice issue date',
    },
    frozenCellValue: 'Frozen cell value',
  },
  headcount: {
    title: 'Hiring plan',
    description:
      'Heads and personnel cost by month and department — the plan for forward months, payroll for realized ones. People with no Posição appear under Unplanned.',
    entity: 'Entity',
    from: 'From',
    to: 'To',
    breadcrumbFpa: 'FP&A',
    department: 'Department',
    departmentOptional: 'Department (optional)',
    noDepartment: 'No department',
    unplanned: 'Unplanned',
    heads: '{{count}} HC',
    visualization: 'Visualization',
    visualizationOptions: {
      costAndHeadcount: 'Cost + HC',
      costOnly: 'Cost only',
      headcountOnly: 'Headcount only',
    },
    empty: 'No positions in this range.',
    legend:
      '— on a department row = position not started yet or outside the period. On Current team: — = month already realized (payroll result) or no earlier posted payroll to carry.',
    inactiveCell: 'Position not started yet or outside the period',
    encargosHint:
      'Monthly cost = salary × {{multiplier}} (statutory charges {{rate}}% + 13th-salary/vacation provisions).',
    encargosPolicyDefault: 'tenant default policy',
    encargosPolicyConfigured: 'tenant-configured rates',
    currentTeamUnplannedBadge: 'not covered by the plan this month',
    currentTeamHeaderTooltip:
      'Carry-forward of the last posted payroll before each month, on the plan employer-cost basis — salary plus statutory charges and 13th-salary/vacation provisions, which is why it sits above the payroll gross. CLT only: PJ collaborators appear on their own row. Not plan data — no terminations or raises.',
    currentTeamTooltip:
      'Carry-forward of the payroll posted in {{period}}. Not plan data — no terminations or raises.',
    currentTeamEmpty: 'No posted monthly payroll to carry the current team forward.',
    realizedMonthDash:
      'Month already realized — the current team is in the payroll result, not the carry-forward.',
    subtotalPlanned: 'Planned',
    subtotalCurrentTeam: 'Current team (CLT)',
    subtotalCurrentTeamPj: 'Current team (PJ)',
    currentTeamPjHeaderTooltip:
      'Active recurring bills for collaborator vendors with the Payroll nature. The contracted amount has no CLT charges and appears only within the recurring bill’s start and end dates.',
    currentTeamPjTooltip: 'Contracted amount from the Payroll recurring bill.',
    subtotalGrandTotal: 'Grand total',
    end: 'End',
    endOptional: 'End (optional)',
    openEnded: 'Open-ended',
    error: 'Failed to load the hiring plan.',
    positions: 'Positions',
    positionTitle: 'Title',
    start: 'Start',
    salary: 'Salary',
    status: 'Status',
    statusValues: {
      PLANNED: 'Planned',
      HIRED: 'Hired',
      CANCELLED: 'Cancelled',
    },
    addPosition: 'Add position',
    edit: 'Edit',
    editDialogTitle: 'Edit position',
    editDialogDescription:
      'Correct the planned position title, salary, department, or timing. Hire confirmation and cancel stay on the row actions.',
    savePosition: 'Save position',
    cancelEdit: 'Cancel',
    cancel: 'Cancel',
    cancelDialogTitle: 'Cancel position',
    cancelDialogDescription:
      'This cancels the position "{{title}}". It will no longer appear in the hiring plan and this action cannot be undone.',
    cancelDialogConfirm: 'Cancel position',
    discardDialogTitle: 'Discard unsaved position?',
    discardDialogDescription:
      'You have unsaved changes on this position. Closing now discards them.',
    discardDialogConfirm: 'Discard',
    suggestions: 'Hire suggestions',
    employee: 'Employee',
    employeeOrContract: 'Employee/Contract',
    contractValue: 'Value',
    contractValueDelta: '{{delta}} vs. plan',
    score: 'Score',
    confirmHire: 'Confirm hire',
    linkAndHire: 'Link and hire',
    changeLink: 'Change link',
    changeLinkDialogTitle: 'Change contract link',
    changeLinkDialogDescription:
      'Pick a different vendor and Payroll recurring bill to link to this hired position.',
    currentContract: 'Current contract',
    currentContractUnknown: 'Linked contract',
    contractEnded: 'Contract ended',
    changeLinkVendorLabel: 'Vendor',
    changeLinkContractLabel: 'Recurring bill',
    changeLinkConfirm: 'Confirm link',
    mutationError: 'Could not save the position.',
    confirmHireError: 'Could not confirm the hire.',
    retry: 'Retry',
    toast: {
      createSuccess: 'Position created successfully.',
      updateSuccess: 'Position updated successfully.',
      cancelSuccess: 'Position cancelled successfully.',
      confirmHireSuccess: 'Hire confirmed successfully.',
      linkAndHireSuccess: 'Contract linked and position hired successfully.',
      relinkSuccess: 'Contract link updated successfully.',
    },
    errors: {
      salaryInvalid:
        'Salary must be a positive amount. Use 1500.00 — negative or empty values are not accepted.',
      endBeforeStart: 'The end month must be on or after the start month.',
      titleInvalid: 'Enter a title for the position.',
      periodInvalid: 'Use the YYYY-MM format, for example 2026-03.',
      hireConfirmInvalid:
        'Could not confirm the hire. The position must be planned and the employee available.',
      planExists: 'This entity already has a hiring plan. Open the existing plan.',
      refOutOfScope: 'That position or employee does not belong to this entity.',
      refInactive:
        'The selected department or cost center is inactive. Choose an active one and try again.',
      currencyMismatch:
        "The salary is in a different currency than this entity's book. Adjust the currency or the entity.",
      multiCurrencyScope:
        'The selected entities use different currencies, so there is no single total. Pick one entity to see the amounts.',
      pjFrequencyUnsupported:
        'A Payroll recurring bill must use a Monthly frequency. Change the recurrence to Monthly.',
      currencyUnresolved:
        "Could not determine the plan's currency. Check that the entity has a ledger book assigned.",
      salaryRaiseInvalid:
        'The raise is a fraction: use 0.05 for 5%. Whole numbers like 2 or 5 are read as 200%/500% and are not accepted; the rate must be greater than −1 and less than 10.',
      periodsLimit:
        'The range has more months than the report can show. Shorten the period (maximum {{limit}} months).',
      encargosJurisdiction:
        'Brazilian statutory charges do not apply to this entity. Remove it from the selection or pick another entity.',
      encargosEntityPolicyNotEffective:
        "This entity has its own encargos policy, but no window covers the requested period. Configure a rate for the period or adjust the report's range.",
      scenarioCurrencyUnresolved:
        "Could not determine this scenario's currency. Check that the selected entities have a ledger book.",
      foreignKey:
        'The referenced record does not exist or is out of scope. Check the entity or the employee.',
      uniqueConstraint:
        'A record with these details already exists. Open the existing one instead of creating another.',
      pjLinkTypeMismatch:
        "PJ positions hire by linking a recurring bill; CLT positions hire by linking an employee. Check the position's hiring type.",
      pjLinkContractInvalid:
        'That recurring bill is not an active Payroll template in this entity. Choose a different recurring bill.',
      pjLinkVigenciaMismatch:
        "That recurring bill's coverage dates do not include the position's start month. Adjust the dates or choose a different contract.",
      pjLinkAlreadyLinked:
        'That recurring bill is already linked to another position. Each contract can be linked to only one position.',
    },
  },
  assumptions: {
    errors: {
      generic: 'Could not save the Premissa.',
      valueInvalid: 'The Premissa value must be a decimal number. Example: 0.05 for 5%.',
      valueInvalidScale:
        'The Premissa value accepts at most 6 decimal places. Example: 0.05 for 5%.',
      valueInvalidMagnitude:
        'That Premissa value is too large. Use a number with fewer than 14 integer digits.',
      keyInvalid:
        'The Premissa key uses only lowercase letters, digits, and underscores. Example: churn_pct.',
      keyTaken:
        'A Premissa with this key already exists in this scenario. Pick another key or edit the existing one.',
      nameInvalid: 'Enter a name for the Premissa.',
      unitInvalid: 'Enter the Premissa unit (for example rate, reais, or people).',
      scenarioRequired: 'Bind the Premissa to a budget or a forecast rule set.',
      scenarioAmbiguous:
        'The Premissa cannot belong to a budget and a forecast rule set at the same time. Choose one.',
      inUse: 'This Premissa is used in a formula. Remove the formula reference before deleting.',
      unresolved:
        'The formula points at a Premissa that does not exist in this scenario. Create the Premissa or fix the formula.',
      closedPeriod:
        'That month is already closed or frozen in a snapshot. To change it, request a restatement with a reason.',
      overrideAmountInvalid: 'The adjustment amount must be a decimal number.',
      overrideAmountInvalidScale: 'The adjustment amount accepts at most 6 decimal places.',
      overrideAmountInvalidMagnitude:
        'That adjustment amount is too large. Use a number with fewer than 14 integer digits.',
      overrideCurrencyMismatch:
        "The adjustment is in a different currency than the report. Recreate it in the book's currency.",
      overrideOverlap:
        'An adjustment for this line already covers that period. Change the range or remove the earlier adjustment.',
      overrideRangeInvalid: 'The adjustment end month must be on or after the start month.',
      overrideScopeRequired: 'Set the entity and the book for the adjustment.',
      overrideRollupInvalid: 'The chosen line does not exist or was archived. Pick another line.',
      overrideRollupKindInvalid: 'A subtotal cannot be pinned. Adjust a detail line instead.',
      overridePopulationUndetermined:
        'Could not determine the accounts for this line, so the adjustment was not applied. Use a line with explicit accounts.',
      forecastFilterInvalid:
        'The forecast-rule filter points at an invalid record. Fix the filter on the rule set.',
      foreignKey:
        'The referenced record does not exist or is out of scope. Check the entity, the line, or the employee.',
      uniqueConstraint:
        'A record with these details already exists. Open the existing one instead of creating another.',
    },
  },
  derivedLimits: {
    title: 'Derived limits',
    description:
      'The rule is metadata only. Approval stamps the derived value as the static period limit.',
    fallbackLine: 'The Outros fallback line cannot carry a derivation rule. Use a classified line.',
    formula: {
      label: 'Derivation rule',
      empty: 'No rule on this line. Build a formula from Premissas and Métricas.',
      readOnly: 'This formula cannot be edited here. Save again to replace it.',
      addAssumption: 'Premissa',
      addMetric: 'Métrica',
      addNumber: 'Number',
      inputs: 'Assumptions / Metrics',
      operators: 'Operators',
      numberPlaceholder: 'Value',
      pickAssumption: 'Choose a Premissa',
      pickMetric: 'Choose a Métrica',
      noAssumptions: 'No Premissas on this budget.',
      noMetrics: 'No Métricas available.',
      save: 'Save rule',
      clear: 'Remove rule',
      saved: 'Derivation rule saved.',
      cleared: 'Derivation rule removed.',
      invalid: 'The formula is incomplete. Finish the expression before saving.',
      removeToken: 'Remove token',
    },
    preview: {
      title: 'Derived vs current limit',
      period: 'Period',
      current: 'Current',
      derived: 'Derived',
      currentLimit: 'Current limit',
      derivedLimit: 'Derived limit',
      inputs: 'Metric / Assumptions',
      status: 'Status',
      empty: 'No periods to review in this range.',
      unresolved: 'Unresolved Premissa/Métrica',
      inputsToggle: 'Show Premissas and Métricas',
      inputsHide: 'Hide Premissas and Métricas',
      assumption: 'Premissa',
      metric: 'Métrica',
      inputsEmpty: 'No resolved Premissa or Métrica for this period.',
    },
    annotations: {
      PARENT_SUM_EXCEEDS:
        'Child lines already sum to {{childrenSum}}, which exceeds the parent limit being stamped ({{parentAmount}}).',
    },
    status: {
      drifted: 'Pending',
      inSync: 'In sync',
      unresolved: 'Unresolved',
      unstampable: 'not stampable: zero/negative value',
    },
    approve: {
      action: 'Approve derived limits',
      none: 'No resolved drifted periods to approve.',
      selfApproval: 'The author of the current rule cannot approve this limit.',
      success: 'Derived limits stamped on the line.',
      loadError: 'Could not load the derivation rule or preview.',
      unstampableHint:
        'Some periods drifted, but the derived value is zero or negative and cannot be stamped.',
      partialFailure: 'Periods {{stamped}} were stamped. Periods {{failed}} failed: {{reason}}',
    },
    confirm: {
      title: 'Approve derived limits',
      description:
        'Review the current limit and the derived value per period. Approval stamps exactly the amount you are looking at.',
      confirm: 'Approve and stamp',
      cancel: 'Back',
      period: 'Period',
      current: 'Current',
      derived: 'New',
      none: '—',
    },
    queue: {
      title: 'Pending approvals',
      description: 'Lines on this budget whose derived limit differs from the current amount.',
      open: 'Derived-limit queue',
      empty: 'No lines have a derived limit waiting for approval.',
      driftedCount_one: '{{count}} pending period',
      driftedCount_other: '{{count}} pending periods',
      openLine: 'Open line',
      lineError: 'Could not load the preview for this line.',
    },
    errors: {
      formulaInvalid:
        'The derivation formula is invalid. Use only Premissas and Métricas (no rollups), no extra keys, within the size bound.',
      amountInvalid:
        'The derived value cannot persist as DECIMAL(18,2). Adjust the formula or Premissas to at most two decimal places.',
      zeroNotStampable:
        'A derived 0.00 cannot be stamped: the current model treats zero as unlimited and falls back to the line amount. Use a positive value.',
      expectedMismatch:
        'The derived value changed since review. Reload the suggestion and approve the amount you are looking at.',
      notActive:
        'Stamp limits on the original budget being prepared or the active budget. Reforecasts must be approved before stamping.',
      noRule: 'Attach a derivation rule to this line before approving a limit.',
      unresolved:
        'Some periods have an unresolved Premissa or Métrica. The whole range was refused; see details.periods.',
      periodOutsideLine: 'The requested periods fall outside the budget line window.',
      rangeInverted: 'The start period must be on or before the end period.',
      lineOwnerForbidden: 'Only the budget owner or an authorized line owner can stamp this limit.',
      lineOwnerLimit: 'The stamped range total exceeds this owner’s maximum.',
      budgetArchived: 'Archived budgets cannot carry a derivation rule or stamp.',
      fallbackLine:
        'The Outros fallback line cannot carry a derivation rule. Use a classified line.',
      closedPeriod:
        'Closed or reported periods are immutable through this API. Use the restatement flow.',
      budgetLineReadRequired:
        'Viewing the approved limit requires budget:line:read in addition to fpa:read.',
      currencyMismatch:
        'An operand is in a different currency than the line. Derived limits never convert — fix the metric or Premissa.',
      periodsMissing:
        'A period in the requested range is missing from the line calendar. The whole range was refused; see details.periods.',
      selfApproval:
        'Approval requires a second user. The author of the current derivation rule cannot stamp the limit.',
      ruleAuthorRequired:
        'Save the derivation rule again before approving. This copy has no recorded author.',
      stampedPeriodConflict:
        'This change would drop a stamped period. Send dropStampedPeriodIds naming each period to remove.',
    },
  },
  forecastRuleSets: {
    title: 'Forecast rule sets',
    description:
      'The rules the Projections column uses: recurring bills, open documents, ad-hoc residual, the hiring plan, and payroll run-rate.',
    manage: 'Manage rule sets',
    newSet: 'New rule set',
    empty: 'No rule sets yet. Create the first one to enable the Projections column.',
    emptyEditor: 'Select a rule set or create a new one.',
    name: 'Name',
    descriptionLabel: 'Description',
    status: 'Status',
    statusValues: {
      ACTIVE: 'Active',
      ARCHIVED: 'Archived',
    },
    changeReason: 'Change reason',
    setDetails: 'Rule set',
    accountsEntity: 'Entity (GL accounts)',
    selectEntityPlaceholder: 'Select entity',
    selectEntityForAccounts: 'Select an entity to see the GL accounts.',
    changelog: {
      open: 'View history',
      title: 'Change history',
      description: 'Saved changes to this rule set.',
      emptyTitle: 'No changes recorded',
      emptyDescription: 'The history appears after the rule set is saved.',
      loadError: 'Failed to load the change history.',
      created: 'Rule set created',
      updated: 'Rule set updated',
      deleted: 'Rule set deleted',
      noReason: 'No reason provided',
    },
    rules: 'Rules',
    addRule: 'Add rule',
    removeRule: 'Remove rule',
    remove: 'Remove',
    removeRuleConfirmTitle: 'Remove this rule?',
    removeRuleConfirm:
      'The rule will be removed from this draft. Save the rule set to apply the change.',
    ruleKind: 'Kind',
    ruleStatus: 'Rule is active',
    kindValues: {
      RECURRING_BILLS: 'Recurring bills',
      OPEN_DOCUMENTS: 'Open documents',
      ADHOC_RESIDUAL: 'Ad-hoc residual',
      HEADCOUNT_PLAN: 'Hiring plan',
      PAYROLL_RUNRATE: 'Payroll run-rate',
    },
    kindHints: {
      RECURRING_BILLS: 'Projects recurring accounts-payable templates.',
      OPEN_DOCUMENTS: 'Projects unposted bills and/or the open remainder of purchase orders.',
      ADHOC_RESIDUAL: 'Projects the average residual spend over trailing months.',
      HEADCOUNT_PLAN: 'Projects the cost of the hiring plan.',
      PAYROLL_RUNRATE:
        'Projects recurring payroll cost from the latest payroll run and mapped PJ collaborators. Cannot coexist with the hiring plan.',
    },
    kindHelp: {
      ADHOC_RESIDUAL: {
        whatTitle: 'What it is',
        what: 'Spend that comes from neither a recurring bill nor a purchase order — everyday one-off purchases.',
        howTitle: 'How it is calculated',
        howIntro: 'In three steps.',
        step1Title: '1. Historical average.',
        step1:
          'We add up this spend over the last complete months and take the average. The current month is left out, because it has not ended yet and would drag the average down. You choose how many months to use in "Trailing months".',
        step2Title: '2. We remove what another rule already explains.',
        step2:
          'If the "Recurring bills" rule is on, rent coming from a template leaves the average — that rule already projects the rent. If it is off, rent stays in the average, because otherwise it would not show up anywhere. That is what prevents double counting.',
        step3Title: '3. We remove what has already been posted this month.',
        step3:
          'The column sits next to Realizado, so it only shows what is still expected to happen.',
        exampleTitle: 'Example.',
        exampleIntro: 'Average of R$ 1,000 per month in office expenses. Today is August.',
        tableMonth: 'Month',
        tablePosted: 'Already posted',
        tableProjected: 'Projected',
        monthJune: 'June',
        monthJuly: 'July',
        monthAugust: 'August',
        monthSeptember: 'September',
        conclusion:
          'June already closed above the average, so there is nothing left to project. It never goes negative: a month you overspent projects R$ 0, never a credit.',
      },
    },
    trailingMonths: 'Trailing months',
    includeUnpostedBills: 'Include unposted bills',
    includeOpenPurchaseOrders: 'Include open purchase orders',
    filterInclude: 'Include only',
    filterExclude: 'Exclude',
    filterAll: 'No filter — every source of this kind',
    startTypingToSearch: 'Start typing to search',
    selectorKeys: {
      vendorIds: 'Vendors',
      recurringBillTemplateIds: 'Recurring-bill templates',
      purchaseOrderIds: 'Purchase orders',
      managerialAccountIds: 'Managerial accounts',
      glAccountIds: 'GL accounts',
      costCenterIds: 'Cost centers',
      departmentIds: 'Departments',
    },
    dimensions: 'By dimensions',
    addId: 'Add',
    idPlaceholder: 'UUID',
    save: 'Save rule set',
    delete: 'Delete',
    deleteConfirmTitle: 'Delete this rule set?',
    deleteConfirm:
      'The name becomes available for a new set. Frozen reports stay explainable. Deletion is refused while a template still points at this set.',
    cancel: 'Cancel',
    archivedBanner: 'This set is archived and does not appear in the Projections picker.',
    saved: 'Rule set saved.',
    deleted: 'Rule set deleted.',
    loadError: 'Failed to load forecast rule sets.',
    retry: 'Retry',
    unknownVariantTitle: 'Unknown variant',
    unknownVariant:
      'This rule kind cannot be edited on this screen yet. The rule parameters are preserved when you save.',
    discardDraftTitle: 'Discard changes?',
    discardDraftBody:
      'This rule set has unsaved changes. If you continue, those changes will be lost.',
    discardDraftConfirm: 'Discard',
    discardDraftCancel: 'Keep editing',
    draftIssue: {
      NAME_REQUIRED: 'Enter a name for the rule set — it becomes the column header.',
      NAME_TOO_LONG: 'The name must be at most 255 characters.',
      TRAILING_MONTHS: 'The residual window must be an integer between 1 and 60 months.',
      OPEN_DOCUMENTS_EMPTY:
        'An open-documents rule must project unposted bills, open purchase orders, or both.',
      SELECTOR_ID_INVALID: 'Each filter value must be a UUID. Remove or fix the invalid id.',
      PAYROLL_HEADCOUNT_COACTIVE:
        'Payroll run-rate and the hiring plan cannot share a rule set. Remove one of them.',
      PAYROLL_DUPLICATE: 'A rule set may contain at most one payroll run-rate rule.',
    },
    errors: {
      generic: 'Could not save the forecast rule set.',
      nameTaken:
        'A forecast rule set with this name already exists. Pick another name or edit the existing set.',
      nameInvalid: 'Enter a forecast rule set name of at most 255 characters.',
      inUse:
        'This set is still used by a template. Remove the column that points at it before deleting.',
      paramsInvalid:
        'These parameters do not belong to this rule kind, or a value is out of range (residual window 1–60 months; open documents must include bills, purchase orders, or both).',
      filterInvalid:
        'The filter is incomplete or invalid. Use include/exclude with at least one UUID, or omit the filter for “all sources”.',
      ruleIdInvalid: 'The rule identifier is invalid. Reload the set and try again.',
      ruleIdDuplicate: 'The same rule appears more than once. Remove the duplicate.',
      ruleIdUnknown: 'A named rule does not belong to this set. Remove the identifier or reload.',
      ruleIdOnCreate: 'Do not send a rule identifier when creating a set. Save first, then edit.',
      variantImmutable:
        'An existing rule’s kind cannot change. Remove the rule and add a new one of the new kind.',
      foreignKey:
        'The referenced record does not exist or is out of scope. Check the filter or the set.',
      uniqueConstraint:
        'A record with these details already exists. Open the existing one instead of creating another.',
      payrollHeadcountCoactive:
        'Payroll run-rate and the hiring plan cannot be active in the same rule set. Remove one of them.',
      payrollDuplicate: 'A rule set may contain at most one payroll run-rate rule.',
    },
  },
  snapshots: {
    freeze: 'Freeze',
    freezeTitle: 'Freeze this report',
    freezeDescription:
      'Creates an immutable snapshot of the evaluated result — an audit artifact. The live report keeps re-evaluating on every read.',
    freezeConfirm: 'Freeze',
    freezeName: 'Snapshot name',
    freezeNamePlaceholder: 'e.g. Aug 2026 close',
    freezePeriod: 'Period',
    freezeTemplate: 'Template',
    freezeScenario: 'Scenario',
    freezeSuccess: 'Snapshot frozen.',
    freezeDisabled: 'Select entities and a valid period to freeze the report.',
    openList: 'Snapshots',
    listTitle: 'Snapshots',
    listDescription: 'Frozen artifacts of this template. Open one as-of or compare two points.',
    listEmptyTitle: 'No snapshots',
    listEmptyDescription:
      'Freeze the report you are looking at to create the first audit artifact.',
    listError: 'Failed to load snapshots.',
    retry: 'Retry',
    manual: 'Manual',
    daily: 'Daily',
    viewAsOf: 'View as-of',
    asOfDate: 'As-of date',
    asOfDateAction: 'Open as-of',
    asOfBanner: 'Viewing snapshot {{name}} (frozen {{date}})',
    backToLive: 'Back to live',
    diffVsLive: 'Live',
    diffFrom: 'From',
    diffTo: 'To',
    diffAction: 'Open diff',
    diffBanner: 'Diff: {{from}} vs {{to}}',
    diffEmptyTitle: 'No cells changed',
    diffEmptyDescription: 'Both points have the same values on every line.',
    diffError: 'Could not compute the diff.',
    columns: {
      line: 'Line',
      period: 'Period',
      from: 'From',
      to: 'To',
      delta: 'Delta',
      column: 'Column',
    },
    becameAvailable: 'Became available',
    becameUnavailable: 'Became unavailable',
    unitChanged: 'Unit changed',
    periodRange: '{{from}} – {{to}}',
    errors: {
      generic: 'Could not complete the snapshot operation.',
      notFound: 'No snapshot was found for that date or identifier.',
      dateInvalid: 'The snapshot date must be in YYYY-MM-DD format.',
      scenarioNotDaily:
        'That scenario can only be frozen as a manual snapshot. The daily lane captures Realizado only.',
      scenarioRefInvalid:
        'The scenario reference is incomplete. Orçado/Reforecast needs a budget; Projections needs a forecast rule set.',
      scopeMismatch:
        'The two snapshots freeze different scopes ({{dimensions}}). A delta would report a scope change as movement.',
      cellsInvalid: 'The frozen artifact is corrupt and cannot be read.',
      captureTimeout:
        'The freeze exceeded the evaluation budget. Retry with a shorter period or fewer entities.',
      templateArchived: 'Archived templates cannot be frozen.',
      uniqueConstraint: 'A record with these details already exists. Open the existing one.',
      foreignKey: 'The referenced record does not exist or is out of scope.',
      dimensions: {
        entities: 'entities',
        book: 'book',
        scenario: 'scenario',
        entitiesOrBook: 'entities or book',
      },
    },
    freezeEntities: 'Entities',
    freezeBook: 'Book',
    freezeDisabledAsOf: 'Leave as-of view to freeze the live report.',
    freezeDisabledTemplateColumns:
      'Freeze captures only the Realizado column — leave template-columns mode to freeze.',
    freezeBookAmbiguous: 'Could not determine the report book — reload before freezing.',
    bookUnverified: 'not returned',
    diffUnverifiedBanner: 'Snapshot: {{snapshot}} — Live: {{live}}',
    scopeEntities: 'entities {{names}}',
    scopeScenario: 'scenario {{kind}}',
    scopeBook: 'book {{book}}',
    diffPairReordered: 'From/To were swapped into chronological order.',
    diffUnlabeledRow: 'Line',
    asOfScope: 'Snapshot scope',
    asOfFiltersLocked: 'Live report filters are paused while you view the artifact.',
  },
  builder: {
    title: 'P&L template builder',
    createTitle: 'New P&L report',
    openBuilder: 'Open builder',
    save: 'Save template',
    saving: 'Saving…',
    saved: 'Template saved',
    discard: 'Discard changes',
    openReport: 'Open report',
    genericError: 'Could not save the template.',
    tabs: {
      rows: 'Rows',
      columns: 'Columns',
      display: 'Display',
      modelagem: 'Modeling',
    },
    list: {
      create: 'New report',
      archive: 'Archive',
      archiveTitle: 'Archive template',
      archiveConfirm:
        'Archive this template? It leaves the active list and can no longer be edited.',
    },
    wizard: {
      stepIdentity: 'Identity',
      stepConfirm: 'Confirm',
      next: 'Continue',
      back: 'Back',
      create: 'Create report',
      name: 'Name',
      namePlaceholder: 'e.g. Managerial P&L 2026',
      description: 'Description',
      regime: 'Regime',
      fonte: 'Data source',
      dateBasis: 'Date basis',
      start: 'Start from',
      startSeed: 'Standard P&L (official rows)',
      startBlank: 'Blank template',
      identityWarning:
        'Regime, source, and date basis cannot be changed after creation. Choose carefully.',
      caixaHidesDateBasis: 'Cash templates have no date basis — periods use settlement date.',
      contabilGerencialCaution:
        "With the Contábil source, the Gerencial date basis uses the document's issue date — the report no longer ties to the trial balance for a given month.",
      confirmHint: 'Confirm the identity. This choice cannot be changed later.',
      confirmCheck: 'I understand regime, source, and date basis cannot be changed.',
      seedUnavailable:
        'Official Standard P&L rollups are not available for this source. The template was created blank.',
    },
    canvas: {
      title: 'Row canvas',
      addRow: 'Add row',
      addHeader: 'Add header',
      addSpacer: 'Add spacer',
      moveUp: 'Move up',
      moveDown: 'Move down',
      remove: 'Remove row',
      indent: 'Indent',
      outdent: 'Outdent',
      pickRollup: 'Choose rollup',
      newAggregation: 'New account rollup',
      newCalculated: 'New calculated rollup',
      editRollup: 'Edit rollup',
      noRollup: 'No rollup',
      formula: 'Formula',
      inverted: 'Invert sign (costs/expenses)',
      bold: 'Bold',
      hide: 'Hide row',
      empty: 'No rows. Add a section header or a rollup row.',
      headerPlaceholder: 'Section label',
      rowLabel: 'Row label',
      rowLabelPlaceholder: 'Optional label (preserved on save)',
    },
    columns: {
      title: 'Default columns',
      add: 'Add column',
      remove: 'Remove column',
      kind: 'Kind',
      from: 'From',
      to: 'To',
      label: 'Label',
      scenario: 'Scenario (budget)',
      forecastRuleSet: 'Forecast rule set',
      renewalTerms: 'Assumed renewals',
      dynamicPtax: 'Dynamic PTAX',
      dynamicPtaxHelp:
        'Remeasures recognized-but-unsettled revenue (a Contract Asset) at today’s PTAX. Settled revenue is never retranslated.',
      rangeMode: {
        label: 'Period',
        fixed: 'Fixed',
        rolling: 'Rolling',
      },
      anchor: {
        label: 'Anchor',
        lastClosedMonth: 'Last closed month',
        currentMonth: 'Current month',
      },
      fromOffset: 'From (months)',
      toOffset: 'To (months)',
      resolvedPreview: '{{from}} → {{to}} today',
      rollingPreset: {
        action: 'Rolling forecast',
        title: 'Apply Rolling forecast',
        description:
          'Replaces the current columns with Actual (-6 to 0) and Projection (+1 to +12), anchored to the last closed month.',
        selectRuleSet: 'Select a forecast rule set',
        noRuleSets: 'No active forecast rule set is available.',
        cancel: 'Cancel',
        apply: 'Apply preset',
      },
      empty: 'No columns. Preview uses the report default window.',
      issues: {
        budget: 'Orçado requires an ORIGINAL budget.',
        budgetRevision: 'Reforecast requires a budget revision.',
        forecastRuleSet: 'This scenario requires a forecast rule set.',
        caixaScenario: 'Cash templates only accept the Realizado scenario.',
        tooMany: 'At most 8 columns.',
        periods: 'Each column needs 1 to 120 monthly periods.',
        relativeRange: 'Use whole months from -60 to +60, with “From” before or equal to “To”.',
        tooManyEntities: 'Evaluation accepts at most 50 entities.',
      },
    },
    display: {
      title: 'Display',
      showZeroRows: 'Show zero rows',
      indentNested: 'Indent nested rows',
      percentDecimals: 'Percent decimal places',
    },
    preview: {
      title: 'Live preview',
      loading: 'Evaluating template…',
      empty: 'Save the template and add rows to see the preview.',
      hoverHint: 'Hover a row to highlight the canvas and preview together.',
      missingMetric: 'Metric has no point in this period — the cell was left empty.',
      missingOperand: 'A missing operand enters as 0.',
      divisionByZero: 'Division by zero enters as 0.',
      columnIssues: 'Fix the columns before evaluating the preview.',
      // Fallback for a preview evaluation that failed with no recognised stable code.
      // The preview NEVER saves, so the builder's save-failure copy must not be reused.
      evaluationFailed: 'Could not calculate the preview. Try again.',
      scale: {
        fullWithCurrency: 'Amounts in {{symbol}}',
        thousandsWithCurrency: 'Amounts in {{symbol}} thousands',
        millionsWithCurrency: 'Amounts in {{symbol}} millions',
        full: 'Scale: full',
        thousands: 'Scale: thousands',
        millions: 'Scale: millions',
      },
    },
    consent: {
      title: 'Restatement consent',
      description:
        'This change feeds already-reported snapshots. Confirm the opt-in and provide a reason (at least 10 characters).',
      optIn: 'I authorize the change on already-reported periods',
      reason: 'Change reason',
      reasonPlaceholder: 'Describe why the template needs to change…',
      confirm: 'Save with consent',
      cancel: 'Cancel',
    },
    rollupEditor: {
      title: 'Rollup editor',
      name: 'Name',
      description: 'Description',
      kind: 'Rollup kind',
      kindAggregation: 'Account aggregation',
      kindCalculated: 'Calculated formula',
      kindLocked: 'An existing rollup cannot change kind.',
      switchWarn:
        'Switching between aggregation and formula clears the selection or the formula. Continue?',
      accountTypes: 'Account types',
      accountIds: 'Specific accounts',
      format: 'Format',
      parent: 'Parent rollup',
      noParent: 'None',
      save: 'Save rollup',
      create: 'Create rollup',
      cancel: 'Cancel',
      formulaHint:
        'Subtotals are formulas, never the sum of child rows. Missing operand = 0; division by zero = 0.',
      formulaIncomplete: 'Incomplete formula — finish the operands before saving.',
      extraAccountTypes:
        'Retained types (read-only — this editor does not offer these checkboxes).',
      addAssumption: 'Premissa',
      addMetric: 'Metric',
      createAssumption: 'New Premissa',
      createMetric: 'New metric',
    },
    filters: {
      title: 'Filters',
      add: 'Add filter',
      dimension: 'Dimension',
      operator: 'Operator',
      value: 'Value',
      joinAnd: 'AND',
      joinOr: 'OR',
      readOnly:
        'Read-only filter — this editor cannot author this predicate. It will be preserved on save.',
      empty: 'No filters. Without a filter the rollup uses every account in the type/selection.',
      notNullHint: 'NOT_* operators also pass rows with a null value.',
      amountHint: 'Amount filters force line-level evaluation — this can be slower.',
    },
    errors: {
      generic: 'Could not save. Check the data and try again.',
      nameTaken: 'A template with this name already exists in this tenant.',
      rollupInUse: 'This rollup is in use and cannot be archived.',
      rollupRefNotFound: 'A referenced rollup does not exist or is archived.',
      rollupParentCycle: 'The parent rollup would create a cycle.',
      accountRefShape: 'The account reference has an invalid shape.',
      accountSourceMismatch: 'The account source does not match the rollup source.',
      accountNotFound: 'The referenced account does not exist in this tenant.',
      accountDuplicate: 'The same account appears more than once on the rollup.',
      segmentFilterContabil: 'A managerial segment filter is not allowed on a CONTABIL rollup.',
      segmentTextFilterUnsupported:
        'Rollup filters only accept predefined values; free-text values cannot be filtered yet.',
      segmentFilterValueConflict:
        'Use predefined values or free text in the segment filter, not both together.',
      segmentTextValueNotAllowed:
        'This field does not accept free text. Choose a predefined catalog value.',
      segmentPredefinedValueNotAllowed:
        'This field has no value catalog. Remove predefined values from this filter.',
      segmentTextValueTooLong: 'Free text in the filter must be at most 200 characters.',
      formulaRequired: 'A CALCULATED rollup requires a formula.',
      aggregationNoFormula: 'An AGGREGATION rollup cannot have a formula.',
      aggregationFormatNotMonetary:
        'An AGGREGATION rollup sums monetary amounts; PERCENT and NUMBER formats are only valid on CALCULATED rollups. Use CURRENCY or convert to a calculated rollup.',
      calculatedNoSelection: 'A CALCULATED rollup cannot have an account or type selection.',
      formulaInvalid: 'The formula is invalid.',
      formulaTooDeep: 'The formula exceeds the maximum depth of 8.',
      formulaCycle: 'The formula creates a cycle between rollups.',
      formulaDimensionMismatch: 'Formula operands do not share the same dimension.',
      templateImmutableField: 'Regime, source, and date basis cannot be changed after creation.',
      templateArchived: 'Archived templates cannot be changed.',
      dateBasisCaixa: 'Cash templates cannot have a date basis.',
      rowRollupRequired: 'A value row requires a rollup.',
      rowLabelRequired: 'A section header requires a label.',
      rowRollupForbidden: 'A section header cannot point to a rollup.',
      rowSourceMismatch: 'The row rollup does not use the same source as the template.',
      defaultColumnsInvalid: 'The default columns are invalid.',
      relativeRangeInvalid:
        'Review the rolling range: use whole months from -60 to +60 and keep the start before the end.',
      displaySettingsInvalid: 'The display settings are invalid.',
      definitionPeriodReported:
        'This change feeds an already-reported snapshot. Confirm consent and provide a reason.',
      changeReasonRequired:
        'Provide a reason of at least 10 characters to authorize the restatement.',
      tenantBusy: 'Another definition change is in progress for this tenant. Try again.',
      scenarioBookScopeUnsupported:
        'The selected book does not cover this scope. Adjust the entities or the book.',
    },
  },
  modelagem: {
    title: 'Modeling',
    description: 'Premissas, metrics, and line overrides in the same DRE builder flow.',
    assumptions: 'Premissas',
    metrics: 'Metrics',
    overrides: 'Line overrides',
    restatementQueue: 'Requests in this session',
    sessionQueueNote:
      'Pending requests are approved by another user and appear here only until you reload. The approver reviews the metric point itself — the point API does not expose a pending restatement.',
    emptyAssumptions: 'No Premissas in this tenant.',
    emptyMetrics: 'No metric series.',
    emptyOverrides: 'No line overrides on this template.',
    emptyQueue: 'No requests in this session. They disappear on reload.',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    key: 'Key',
    name: 'Name',
    unit: 'Unit',
    provenance: 'Provenance',
    budget: 'Budget',
    forecastRuleSet: 'Forecast rule set',
    values: 'Monthly values',
    fieldHelp: {
      key: 'Identifier used in calculated-rollup formulas: assumption(key). Cannot be changed after creation.',
      name: 'Display name shown in lists and drill-down. Does not affect evaluation.',
      unit: 'Unit shown next to the value in the drill-down (e.g. rate, %, BRL). Convention: "rate" is a fraction — 0.05 means 5%. Does not affect evaluation.',
      budget:
        'Binds the Premissa to a budget: only that budget’s Orçado/Reforecast columns see it. Fill this field OR the rule set — exactly one. Realizado columns never use Premissas.',
      forecastRuleSet:
        'Binds the Premissa to a projection rule set: only that set’s projection columns see it. Fill this field OR the budget — exactly one.',
      provenance:
        'Optional audit note about where the number came from. Informational only; does not affect evaluation.',
      values:
        'One value per line, in the format YYYY-MM=value (e.g. 2026-08=0.05). A missing month has no value — cells that depend on it stay empty.',
    },
    period: 'Period',
    value: 'Value',
    addValue: 'Add month',
    entity: 'Entity',
    currency: 'Currency',
    points: 'Points',
    sources: 'Sources',
    deletePoint: 'Delete point',
    requestRestatement: 'Request restatement',
    pending: 'Pending',
    approved: 'Approved',
    overrideAmount: 'Override amount',
    effectiveFrom: 'Effective from',
    effectiveTo: 'Effective to',
    book: 'Book',
    removeOverride: 'Remove override',
    createOverride: 'Create override',
    overrideFromCell: 'Override this cell',
    noTemplate: 'Open a template to create line overrides.',
    rollup: 'Line (rollup)',
    selectBook: 'Select the book',
    selectRollup: 'Select the rollup',
    bookRequired: 'Choose the book before saving the override.',
    saveDisabledHint: 'Fill in the required fields to save.',
    boundBudget: 'Budget',
    boundForecast: 'Forecast rule set',
    boundUnbound: 'No scenario binding',
    boundEntity: 'Entity',
    sourcesDate: 'Date',
    sourcesSource: 'Source',
    sourcesAmount: 'Amount',
    sourcesForbidden: 'You do not have permission to view point sources (ar:contract:read).',
    sourcesEmpty: 'This point has no billing sources.',
    sourcesEmptyTenant: 'This point is its own source — there are no billing sources.',
    deleteConfirmTitle: 'Delete',
    deleteAssumptionConfirm: 'Delete this Premissa? This cannot be undone.',
    deleteSeriesConfirm: 'Delete this metric series? This cannot be undone.',
    deletePointConfirm: 'Delete this point from the series?',
    deleteOverrideConfirm: 'Remove this line override?',
  },
  metrics: {
    errors: {
      generic: 'Could not save the metric.',
      keyInvalid:
        'The metric key uses only lowercase letters, digits, dots, and underscores. Example: receita_por_cliente.',
      keyReserved: 'That key is reserved for system metrics (billing.*).',
      valueInvalid: 'The metric value must be a decimal number.',
      periodInvalid: 'The metric period must be YYYY-MM.',
      periodFuture: 'A future period cannot be materialized.',
      pointDuplicate: 'A point already exists for that month on this series.',
      seriesReadOnly: 'System (billing) series are read-only.',
      currencyImmutable: 'The series currency cannot change after creation.',
      currencyMixed: 'Series points cannot mix currencies.',
      currencyScopeMismatch: "The metric currency does not match the evaluated scope's currency.",
      unitImmutable: 'The series unit cannot change after creation.',
      unitMixed: 'Series points cannot mix units.',
      closedPeriod:
        'That month is already closed or reported. Request a restatement with a reason and approval.',
      restatementReasonRequired: 'Provide a reason for the metric restatement.',
      restatementNoChange: 'The restatement must change the point value.',
      restatementNotRequired:
        'That month does not require a restatement — edit the point directly.',
      restatementNotPending: 'That restatement is not pending.',
      selfApproval: 'The user who requested the restatement cannot approve it.',
      restatementStale: 'The point changed since the request. Request the restatement again.',
      evidenceMissing: 'Materialization evidence is missing for this billing metric.',
      evidenceMismatch: 'Materialization evidence does not match the point.',
      entityNotFound: 'The metric entity does not exist in this tenant.',
    },
  },
};
